/**
 * ObraDetalle — ficha de una obra PVC.
 *
 * La obra NO es un pipeline manual: es una lista de hitos que se van marcando
 * con hechos (documentos adjuntos + marcas de entrega/balance).
 *   Pasos 1→3 en orden estricto: OP+pedido → preparación R8 → despacho.
 *   Después: entrega y factura en cualquier orden; cobro siempre después de
 *   la factura; balance antes o después de la cobranza.
 *   FINALIZADA cuando está todo.
 */

import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  listenObra,
  listenPedidosPorObraNumero,
  marcarEntregada,
  marcarBalance,
  actualizarObra,
  vincularPedidoAObra,
  desvincularPedidoDeObra,
  vincularPedidoTelaAObra,
  desvincularPedidoTelaDeObra,
  vincularPedidoComplementario,
  desvincularPedidoComplementario,
  agregarDespachoExtra,
  eliminarDespachoExtra,
  subirArchivoObra,
  getArchivoObra,
  getPedidoVinculado,
  eliminarObra,
  guardarVerificacionObra,
  setSustitucionesObra,
  listenGastosObra,
  agregarGastoObra,
  eliminarGastoObra,
  confirmarLecturaOP,
  desconfirmarLecturaOP,
  actualizarMontoPresupuesto,
  listenCobranzas,
  agregarCobranza,
  adjuntarDocCobranza,
  eliminarCobranza,
  editarCobranza,
  listenFacturas,
  agregarFactura,
  eliminarFactura,
  setFacturaANombreDe,
  listenComprasPlaza,
  agregarCompraPlaza,
  eliminarCompraPlaza,
  descartarCambioOP,
  marcarHitoManual,
  eliminarArchivoObra,
  adjuntarPedidoManual,
  eliminarPedidoManual,
  setClienteObra,
  getClientesLista,
  agregarOP,
  eliminarOP,
  setCarpinteroOP,
  setCarpinteroDespachoExtra,
  habilitarFacturacionAnticipada,
  quitarFacturacionAnticipada,
} from "../services/obrasFS";
import { validarDestinoPedido } from "../services/exportFinnegans";
import {
  CARPINTEROS,
  hitosObra,
  esHitoManual,
  facturacionHabilitada,
  opsDeObra,
  archivosOPs,
  hitoHabilitado,
  obraFinalizada,
  obraConGuale,
  faseObra,
  permisosObras,
  accionesPendientesPara,
  progresoObra,
  responsableActual,
} from "../services/estadosObra";
import { ProgresoBar } from "./ObrasTablero";
import BuscarPedidoModal from "../components/BuscarPedidoModal";
import ComparacionModal from "../components/ComparacionModal";
import ExportFinnegansModal from "../components/ExportFinnegansModal";
import CambiosOPModal from "../components/CambiosOPModal";
import ConfirmarOPModal from "../components/ConfirmarOPModal";

// ─────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────

function toDate(ts) {
  if (!ts) return null;
  if (ts?.seconds) return new Date(ts.seconds * 1000);
  const d = ts instanceof Date ? ts : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function formatFechaHora(ts) {
  const d = toDate(ts);
  if (!d) return null;
  return d.toLocaleDateString("es-UY", { day: "2-digit", month: "short" }) +
    " " + d.toLocaleTimeString("es-UY", { hour: "2-digit", minute: "2-digit" });
}

const EVENTO_LABELS = {
  CREADA: "Obra creada",
  PEDIDO_VINCULADO: "Pedido de Finnegans vinculado",
  PEDIDO_DESVINCULADO: "Pedido desvinculado",
  PEDIDO_TELA_VINCULADO: "Pedido de tela (Gualeguay) vinculado",
  PEDIDO_TELA_DESVINCULADO: "Pedido de tela desvinculado",
  DOC_OP: "Pedido OP adjuntado",
  DOC_FACTURACOMPRA: "Factura de compra (accesorios) adjuntada",
  DOC_RECEPCIONCOMPRA: "Recepción de la compra adjuntada",
  DOC_DESPACHO: "Despacho R8 adjuntado",
  DOC_DESPACHOGUALE: "Despacho Gualeguay adjuntado",
  DOC_FACTURA: "Factura adjuntada",
  DOC_COBRO: "Cobranza adjuntada",
  ENTREGADA: "Obra entregada",
  ENTREGA_DESHECHA: "Entrega desmarcada",
  SUSTITUCION: "Reemplazo de producto",
  BALANCE_HECHO: "Balance hecho",
  BALANCE_DESHECHO: "Balance desmarcado",
};

const cardStyle = {
  background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
  padding: "14px 16px", marginBottom: 12,
};

/** Modal genérico para el detalle de un grupo del proceso */
function DetalleModal({ titulo, onClose, children }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 270, padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, padding: 18, width: "100%",
          maxWidth: 700, maxHeight: "88vh", overflowY: "auto",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16 }}>{titulo}</h2>
          <button className="btn btn--ghost" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/** Firma "por X · fecha" para un hito { at, porNombre } */
function Firma({ marca }) {
  if (!marca) return null;
  return (
    <span style={{ color: "#94a3b8", fontSize: 12 }}>
      {marca.porNombre || "—"} · {formatFechaHora(marca.at)}
    </span>
  );
}

/**
 * Etiqueta corta de estado del paso (arriba a la derecha). Un solo color por
 * significado: verde=hecho, ámbar=requiere atención, azul=en curso, gris=neutro.
 */
function EstadoPaso({ texto, tono = "neutro" }) {
  const c = {
    ok:     { bg: "#f0fdf4", color: "#15803d" },
    aviso:  { bg: "#fffbeb", color: "#b45309" },
    activo: { bg: "#eff6ff", color: "#1d4ed8" },
    neutro: { bg: "#f8fafc", color: "#94a3b8" },
  }[tono] || { bg: "#f8fafc", color: "#94a3b8" };
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
      background: c.bg, color: c.color, borderRadius: 6, padding: "2px 9px",
    }}>
      {texto}
    </span>
  );
}

/**
 * Fila de documento: misma estructura en TODOS los pasos.
 * Arriba la etiqueta (qué documento es), abajo el archivo o su estado,
 * y a la derecha siempre las mismas acciones.
 */
function FilaArchivo({ etiqueta, cuerpo, acciones, ultimo }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "7px 0",
      borderBottom: ultimo ? "none" : "1px solid #f1f5f9",
    }}>
      <div style={{ flex: 1, minWidth: 0, fontSize: 12.5 }}>
        <div style={{ color: "#64748b" }}>{etiqueta}</div>
        <div style={{ marginTop: 1, overflowWrap: "anywhere" }}>{cuerpo}</div>
      </div>
      {acciones && <div style={{ flexShrink: 0, display: "flex", gap: 4, alignItems: "center" }}>{acciones}</div>}
    </div>
  );
}

/** Modal: productos de un pedido de Finnegans, sin salir de Obras. */
function ProductosPedidoModal({ pedido, onClose }) {
  const { id, cliente, productos = [], loading, error } = pedido || {};
  const filas = (productos || []).map((it, i) => ({
    key: i,
    cod: it.cod || it.codigo || it.codigoURU || "",
    nombre: it.descripcion || it.desc || it.nombre || it.cod || it.codigo || "—",
    cant: it.cant ?? it.cantidad ?? it.qty ?? "",
  }));
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 300,
        display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 16, overflowY: "auto",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, marginTop: 40,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)", overflow: "hidden",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "14px 18px", borderBottom: "1px solid #f1f5f9" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Pedido {id}</div>
            {cliente && <div style={{ fontSize: 12.5, color: "#64748b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cliente}</div>}
          </div>
          <button className="btn btn--ghost" style={{ fontSize: 18, lineHeight: 1 }} onClick={onClose} title="Cerrar">✕</button>
        </div>

        <div style={{ padding: "12px 18px 18px" }}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.6, marginBottom: 6 }}>
            CONTENIDO DEL PEDIDO{filas.length ? ` · ${filas.length} producto${filas.length === 1 ? "" : "s"}` : ""}
          </div>
          {loading ? (
            <div style={{ fontSize: 13, color: "#94a3b8", padding: "16px 0", textAlign: "center" }}>Cargando productos…</div>
          ) : error ? (
            <div style={{ fontSize: 13, color: "#c2410c", padding: "12px 0" }}>No se pudieron leer los productos: {error}</div>
          ) : filas.length === 0 ? (
            <div style={{ fontSize: 13, color: "#94a3b8", padding: "12px 0" }}>Este pedido no tiene productos cargados.</div>
          ) : (
            <div style={{ display: "grid", gap: 6 }}>
              {filas.map(f => (
                <div key={f.key} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "9px 11px",
                  border: "1px solid #e2e8f0", borderRadius: 9, background: "#f8fafc",
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: "#0f172a", fontWeight: 600, overflowWrap: "anywhere" }}>{f.nombre}</div>
                    {f.cod && <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{f.cod}</div>}
                  </div>
                  {f.cant !== "" && (
                    <div style={{ flexShrink: 0, fontSize: 13, fontWeight: 700, color: "#334155", background: "#e2e8f0", borderRadius: 999, padding: "2px 10px" }}>
                      ×{f.cant}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Bloque "Documentos" de un paso: rótulo + lista de filas */
function SeccionDocs({ titulo = "DOCUMENTOS", children }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#cbd5e1", letterSpacing: 0.6, marginBottom: 1 }}>
        {titulo}
      </div>
      <div style={{ borderTop: "1px solid #f1f5f9" }}>{children}</div>
    </div>
  );
}

/** Tarjeta de paso: solo el paso activo lleva borde azul; el resto, neutro. */
function Paso({ numero, titulo, completo, activo, bloqueado, advertencia, estado, detalle, accion }) {
  const icono = completo ? "✓" : advertencia ? "!" : activo ? "●" : "○";
  const colorIcono = completo ? "#16a34a" : advertencia ? "#d97706" : activo ? "#2563eb" : "#cbd5e1";
  return (
    <div style={{
      background: "#fff",
      border: activo && !completo ? "2px solid #93c5fd" : "1px solid #e2e8f0",
      borderRadius: 12,
      padding: "11px 14px",
      marginBottom: 8,
      opacity: bloqueado && !completo ? 0.6 : 1,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: colorIcono, width: 14, textAlign: "center" }}>{icono}</span>
        <span style={{ fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0, color: completo ? "#475569" : "#0f172a" }}>
          {numero} · {titulo}
        </span>
        {estado}
        {accion && <div style={{ flexShrink: 0 }}>{accion}</div>}
      </div>
      {detalle && <div style={{ fontSize: 12.5, marginTop: 9, paddingLeft: 24 }}>{detalle}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
//  Página
// ─────────────────────────────────────────────────────────────

export default function ObraDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();

  const [obra, setObra] = useState(undefined); // undefined = cargando, null = no existe
  const [candidatos, setCandidatos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showBuscar, setShowBuscar] = useState(false);
  const [showBuscarTela, setShowBuscarTela] = useState(false);
  const [buscarComplementario, setBuscarComplementario] = useState(null); // null | "r8" | "guale"
  // Modal para ver los productos de un pedido de Finnegans sin salir de Obras.
  const [modalPedido, setModalPedido] = useState(null); // { id, cliente, productos, loading, error }
  const [despachoExtraDest, setDespachoExtraDest] = useState(null); // destino elegido para el próximo file
  const despachoExtraRef = useRef(null);
  const [pedidoManualDest, setPedidoManualDest] = useState(null); // destino del pedido adjunto a leer
  const [leyendoPedido, setLeyendoPedido] = useState(false);
  const pedidoManualRef = useRef(null);
  const opFileRef = useRef(null);          // alta de un pedido de OP (etapa)
  const [subiendoOP, setSubiendoOP] = useState(false);
  // Corregir el cliente de la obra (si se eligió mal al crearla)
  const [editandoCliente, setEditandoCliente] = useState(false);
  const [clientesLista, setClientesLista] = useState([]);
  const [qClienteEdit, setQClienteEdit] = useState("");
  const [notasDraft, setNotasDraft] = useState(null);
  const [subiendoDoc, setSubiendoDoc] = useState(null);
  const [verHistorial, setVerHistorial] = useState(false);
  const [comparando, setComparando] = useState(null);   // tipo en proceso
  const [comparacion, setComparacion] = useState(null); // { tipo, resultado }
  const [showExport, setShowExport] = useState(false);
  const [showCambiosOP, setShowCambiosOP] = useState(false);
  const [exportDelta, setExportDelta] = useState(null); // ítems del pedido complementario
  const [showConfirmarOP, setShowConfirmarOP] = useState(false);
  const [confirmandoOP, setConfirmandoOP] = useState(false);
  const opAutoRef = useRef(false);
  const [detalleGrupo, setDetalleGrupo] = useState(null); // "pedidos" | "despachos"
  const [showBalance, setShowBalance] = useState(false);
  const [detallePedidos, setDetallePedidos] = useState(null); // null=cargando · {error} · {items, prodR8, prodGuale, cruce}
  const [detalleDespachos, setDetalleDespachos] = useState(null); // null=cargando · {error} · {grupos:[{label,archivo,items,error}]}
  const [sustituyendo, setSustituyendo] = useState(null); // claveOP en modo "elegir reemplazo"
  const [gastos, setGastos] = useState([]);
  const [nuevoGasto, setNuevoGasto] = useState({ concepto: "", monto: "" });
  const [cobranzas, setCobranzas] = useState([]);
  const [nuevaCobranza, setNuevaCobranza] = useState({ monto: "", fecha: "", nota: "", efectivo: false });
  const [mostrarFormCobranza, setMostrarFormCobranza] = useState(false);
  const [editandoCobranza, setEditandoCobranza] = useState(null); // id de la cobranza en edición
  const [editCobranza, setEditCobranza] = useState({ monto: "", fecha: "", nota: "", efectivo: false });
  const cobranzaFileRef = useRef(null);
  const cobranzaTargetRef = useRef(null); // { id, campo }
  const [comprasPlaza, setComprasPlaza] = useState([]);
  const [nuevaCompra, setNuevaCompra] = useState({ nota: "", monto: "" });
  const [compraFile, setCompraFile] = useState(null);
  const compraFileRef = useRef(null);
  const [facturas, setFacturas] = useState([]);
  const [facturaForm, setFacturaForm] = useState({ blanco: { monto: "", nota: "" }, negro: { monto: "", nota: "" } });
  const [facturaFileByTipo, setFacturaFileByTipo] = useState({ blanco: null, negro: null });
  const facturaRefBlanco = useRef(null);
  const facturaRefNegro = useRef(null);
  // A nombre de quién va la factura (del maestro de clientes de Finnegans)
  const [editandoNombreFactura, setEditandoNombreFactura] = useState(false);
  const [nombreFactura, setNombreFactura] = useState(null); // { nombre, codigo }
  const [qNombreFactura, setQNombreFactura] = useState("");
  const [pidiendoAnticipada, setPidiendoAnticipada] = useState(false);
  // Monto del presupuesto (se carga a mano)
  const [editandoMonto, setEditandoMonto] = useState(false);
  const [montoInput, setMontoInput] = useState("");
  const [monedaInput, setMonedaInput] = useState("USD");
  const [motivoAnticipada, setMotivoAnticipada] = useState("");
  const [isDesktop, setIsDesktop] = useState(() => typeof window !== "undefined" && window.innerWidth >= 900);

  useEffect(() => {
    const h = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  const fileInputRef = useRef(null);
  const tipoDocRef = useRef(null);
  const autoLinkRef = useRef(false);

  const usuario = {
    uid: user?.uid,
    nombre: profile?.nombre,
    displayName: user?.displayName,
    email: user?.email,
  };

  const permisos = permisosObras(profile);
  const puedeEditar = permisos.editar;
  const puedeFinanzas = permisos.finanzas;

  useEffect(() => listenObra(id, setObra), [id]);

  // Gastos de la obra — solo visibles/cargables para finanzas (reglas de Firestore)
  useEffect(() => {
    if (!puedeFinanzas) return;
    return listenGastosObra(id, setGastos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, puedeFinanzas]);

  // Cobranzas, facturas y compras en plaza — todo el staff de obras
  useEffect(() => listenCobranzas(id, setCobranzas), [id]);
  useEffect(() => listenFacturas(id, setFacturas), [id]);
  useEffect(() => listenComprasPlaza(id, setComprasPlaza), [id]);

  // Productos despachados — se leen de los remitos adjuntos al abrir "Ver productos"
  useEffect(() => {
    if (detalleGrupo !== "despachos") { setDetalleDespachos(null); return; }
    let cancel = false;
    (async () => {
      try {
        const { extraerItemsDeAdjunto } = await import("../services/parseArchivosObra");
        const fuentes = [];
        const r8 = getArchivoObra(obra, "despacho");
        if (r8) fuentes.push({ label: "Despacho R8", destino: "r8", archivo: r8 });
        const gu = getArchivoObra(obra, "despachoGuale");
        if (gu) fuentes.push({ label: "Despacho Gualeguay", destino: "guale", archivo: gu });
        for (const d of (obra.despachosExtra || [])) {
          if (d.archivo) fuentes.push({ label: `Remito adicional ${d.destino === "r8" ? "R8" : "GUALE"}`, destino: d.destino, archivo: d.archivo });
        }
        const grupos = [];
        for (const f of fuentes) {
          try {
            grupos.push({ ...f, items: await extraerItemsDeAdjunto(f.archivo, "despacho") });
          } catch (e) {
            grupos.push({ ...f, items: [], error: e.message || String(e) });
          }
        }
        if (!cancel) setDetalleDespachos({ grupos });
      } catch (e) {
        if (!cancel) setDetalleDespachos({ error: e.message || String(e) });
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleGrupo, obra?.archivos?.despacho?.url, obra?.archivos?.despachoGuale?.url, JSON.stringify(obra?.despachosExtra || [])]);

  // Maestro de clientes — se carga cuando se necesita el buscador: al editar el
  // cliente de la obra, o al definir/editar el plan de facturación ("a nombre de").
  const necesitaClientes = editandoCliente || editandoNombreFactura;
  useEffect(() => {
    if (!necesitaClientes || clientesLista.length > 0) return;
    getClientesLista().then(setClientesLista).catch(err => console.warn("[clientes]", err));
  }, [necesitaClientes, clientesLista.length]);

  // Total de accesorios de plaza según el OP (para verificar la recepción)
  const [opAccesorios, setOpAccesorios] = useState(null); // null | { total, items }
  useEffect(() => {
    if (!obra || archivosOPs(obra).length === 0) return;
    let cancel = false;
    (async () => {
      try {
        const { extraerItemsDeOPs } = await import("../services/parseArchivosObra");
        const items = await extraerItemsDeOPs(archivosOPs(obra));
        const plaza = items.filter(it => it.categoria === "plaza");
        const total = plaza.reduce((s, it) => s + (Number(it.cant) || 0), 0);
        if (!cancel) setOpAccesorios({ total, items: plaza });
      } catch { if (!cancel) setOpAccesorios(null); }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, JSON.stringify(archivosOPs(obra).map(a => a.url))]);

  // Costo de producción = suma (SIN IVA) de los valores de los productos del
  // pedido OP — cada renglón ya trae su total. Solo finanzas.
  const [costoProd, setCostoProd] = useState(null); // null=sin calcular · {total, sinValor}
  useEffect(() => {
    if (!puedeFinanzas || !obra || archivosOPs(obra).length === 0) return;
    let cancel = false;
    (async () => {
      try {
        const { extraerItemsDeOPs } = await import("../services/parseArchivosObra");
        const items = await extraerItemsDeOPs(archivosOPs(obra));
        let total = 0, sinValor = 0;
        for (const it of items) {
          if (Number.isFinite(it.total) && it.total > 0) total += it.total;
          else sinValor++;
        }
        if (!cancel) setCostoProd({ total: Math.round(total * 100) / 100, sinValor });
      } catch (e) {
        if (!cancel) setCostoProd({ error: e.message || String(e) });
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, puedeFinanzas, JSON.stringify(archivosOPs(obra).map(a => a.url))]);

  useEffect(() => {
    if (!obra?.numero) return;
    return listenPedidosPorObraNumero(obra.numero, setCandidatos);
  }, [obra?.numero]);


  const tieneOP = !!(obra && opsDeObra(obra).length > 0);

  // Al adjuntar/tener OP sin confirmar, abrir automáticamente la confirmación
  // de lectura (una sola vez por carga; se re-arma si se reemplaza el OP).
  useEffect(() => {
    if (obra && tieneOP && !obra.opConfirmado && puedeEditar && !opAutoRef.current) {
      opAutoRef.current = true;
      setShowConfirmarOP(true);
    }
    if (obra?.opConfirmado) opAutoRef.current = false; // reset para el próximo reemplazo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obra?.id, tieneOP, obra?.opConfirmado, puedeEditar]);

  // Al abrir el detalle de pedidos: parsear el OP, traer los dos pedidos de
  // Finnegans completos y cruzarlos (¿está TODO el OP en los pedidos?)
  useEffect(() => {
    if (detalleGrupo !== "pedidos") {
      setDetallePedidos(null);
      setSustituyendo(null);
      return;
    }
    (async () => {
      try {
        if (!tieneOP) { setDetallePedidos({ error: "La obra no tiene ningún pedido OP adjunto" }); return; }
        const { extraerItemsDeOPs, compararItemsConPedido } =
          await import("../services/parseArchivosObra");
        const items = await extraerItemsDeOPs(archivosOPs(obra));
        const baseR8 = await productosDePedidos([obra.pedidoId, ...idsComplementarios("r8")]);
        const baseGuale = await productosDePedidos([obra.pedidoTelaId, ...idsComplementarios("guale")]);
        // Sumar los productos LEÍDOS de un pedido adjuntado a mano (admin)
        const r8Merged = [...(baseR8 || []), ...(obra.pedidoAdjuntoR8?.productos || [])];
        const gualeMerged = [...(baseGuale || []), ...(obra.pedidoAdjuntoGuale?.productos || [])];
        const prodR8 = r8Merged.length ? r8Merged : null;
        const prodGuale = gualeMerged.length ? gualeMerged : null;
        const cruce = prodR8?.length
          ? compararItemsConPedido(items, prodR8, prodGuale, obra.sustituciones || null)
          : null;
        setDetallePedidos({ items, prodR8, prodGuale, cruce });
      } catch (e) {
        setDetallePedidos({ error: e.message || String(e) });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detalleGrupo, obra?.pedidoId, obra?.pedidoTelaId, obra?.pedidoAdjuntoR8?.archivo?.path, obra?.pedidoAdjuntoGuale?.archivo?.path, JSON.stringify(obra?.pedidosExtra || []), JSON.stringify(obra?.sustituciones || {})]);

  // Vinculación automática — solo con el OP adjunto Y confirmado por Ceci
  useEffect(() => {
    if (!obra || obra.pedidoId || !puedeEditar || !tieneOP || !obra.opConfirmado) return;
    // Si el usuario desvinculó a propósito, no volver a auto-vincular.
    if (obra.noAutoVincularR8) return;
    const candidato = obra.pedidoSugerido || (candidatos.length === 1 ? candidatos[0] : null);
    if (!candidato || autoLinkRef.current) return;
    autoLinkRef.current = true;
    vincularPedidoAObra(obra, candidato, usuario, { auto: true })
      .then(() => handleComparar("op", { mostrar: "si-dif", pedidoOverride: candidato }))
      .catch(err => {
        console.error("[auto-vincular]", err);
        autoLinkRef.current = false;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obra?.id, obra?.pedidoId, obra?.noAutoVincularR8, tieneOP, obra?.opConfirmado, candidatos.length, puedeEditar]);

  if (obra === undefined) {
    return <div style={{ padding: 30, color: "#64748b" }}>Cargando obra…</div>;
  }
  if (obra === null) {
    return (
      <div style={{ padding: 30 }}>
        <p>No se encontró la obra.</p>
        <button className="btn" onClick={() => navigate("/obras")}>← Volver al tablero</button>
      </div>
    );
  }

  const fase = faseObra(obra);
  const hitos = hitosObra(obra);
  const finalizada = obraFinalizada(obra);
  const conGuale = obraConGuale(obra);
  const misAcciones = accionesPendientesPara(obra, profile);
  const meToca = (hito) => misAcciones.some(a => a.hito === hito);
  const pillTeToca = (
    <span style={{
      fontSize: 9.5, fontWeight: 700, background: "#fef3c7", color: "#92400e",
      borderRadius: 999, padding: "2px 8px", marginLeft: 6, whiteSpace: "nowrap",
      textTransform: "uppercase", letterSpacing: 0.4,
    }}>
      Acción requerida
    </span>
  );

  const pedidoVinculado = obra.pedidoId
    ? candidatos.find(p => p.id === obra.pedidoId) || { id: obra.pedidoId, cliente: obra.pedidoCliente }
    : null;

  // Abre el modal con los productos del pedido. Si el objeto no trae los
  // productos cargados, los busca del pedido completo (sin salir de Obras).
  async function verProductosDelPedido(pedidoId, pedidoObj) {
    if (!pedidoId) return;
    const yaTiene = pedidoObj?.productos?.length > 0;
    setModalPedido({
      id: pedidoId,
      cliente: pedidoObj?.cliente || obra.pedidoCliente || obra.cliente || null,
      productos: yaTiene ? pedidoObj.productos : [],
      loading: !yaTiene,
      error: null,
    });
    if (yaTiene) return;
    try {
      const p = await getPedidoVinculado(pedidoId);
      setModalPedido(m => (m && m.id === pedidoId)
        ? { ...m, productos: p?.productos || [], cliente: p?.cliente || m.cliente, loading: false }
        : m);
    } catch (e) {
      setModalPedido(m => (m && m.id === pedidoId)
        ? { ...m, productos: [], loading: false, error: e.message || String(e) }
        : m);
    }
  }

  function pedirArchivo(tipo) {
    tipoDocRef.current = tipo;
    fileInputRef.current?.click();
  }

  async function handleArchivoSeleccionado(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const tipo = tipoDocRef.current;
    if (!file || !tipo) return;
    setSubiendoDoc(tipo);
    try {
      const url = await subirArchivoObra(obra.id, tipo, file, usuario);
      // Verificación automática del circuito: si hay diferencias, se muestran ya
      if (obra.pedidoId && ["op", "despacho", "despachoGuale"].includes(tipo)) {
        handleComparar(tipo, { mostrar: "si-dif", archivoOverride: { url, nombre: file.name } });
      }
      // Recepción de mercadería: leer la cantidad recibida y compararla con
      // el total de accesorios del OP. Se guarda para mostrar ✓/⚠ + Aceptar.
      if (tipo === "recepcionCompra") {
        try {
          const { extraerItemsDeAdjunto } = await import("../services/parseArchivosObra");
          const recItems = await extraerItemsDeAdjunto({ url, nombre: file.name }, "despacho");
          const cantDoc = recItems.reduce((s, it) => s + (Number(it.cant) || 0), 0);
          const cantOP = opAccesorios?.total ?? null;
          const ok = cantOP != null && Math.abs(cantDoc - cantOP) < 0.001;
          await guardarVerificacionObra(obra.id, "recepcion", {
            ok, cantDoc, cantOP,
            texto: cantOP == null ? `${cantDoc} unid.` : ok ? `${cantDoc} unid. — coincide` : `${cantDoc} vs ${cantOP} en el OP`,
          });
        } catch (errR) {
          console.warn("[verif recepción]", errR);
        }
      }
      // El monto del presupuesto se carga a mano (botón "✎ monto"), no se lee del archivo.
    } catch (err) {
      alert("No se pudo subir el archivo: " + (err.message || err));
    } finally {
      setSubiendoDoc(null);
    }
  }

  async function handleSeleccionPedido(pedido) {
    if (busy) return;
    // Control de destino: ¿este pedido es realmente el de R8?
    const aviso = validarDestinoPedido(pedido.productos, "r8");
    if (aviso && !window.confirm("⚠️ " + aviso)) return;
    setBusy(true);
    try {
      await vincularPedidoAObra(obra, pedido, usuario, { auto: false });
      setShowBuscar(false);
      // Verificación automática al vincular: el OP contra el pedido recién linkeado
      handleComparar("op", { mostrar: "si-dif", pedidoOverride: pedido });
    } catch (err) {
      alert("No se pudo vincular: " + (err.message || err));
    } finally {
      setBusy(false);
    }
  }

  async function handleSeleccionPedidoTela(pedido) {
    if (busy) return;
    // Control de destino: ¿este pedido es realmente el de Gualeguay?
    const aviso = validarDestinoPedido(pedido.productos, "guale");
    if (aviso && !window.confirm("⚠️ " + aviso)) return;
    setBusy(true);
    try {
      await vincularPedidoTelaAObra(obra, pedido, usuario);
      setShowBuscarTela(false);
      // Re-verificar el OP incluyendo el pedido de Gualeguay recién vinculado
      handleComparar("op", { mostrar: "si-dif", pedidoGualeOverride: pedido });
    } catch (err) {
      alert("No se pudo vincular: " + (err.message || err));
    } finally {
      setBusy(false);
    }
  }

  // ── Pedidos complementarios (ampliaciones por cambio de presupuesto) ──
  function idsComplementarios(destino) {
    return (obra?.pedidosExtra || []).filter(p => p.destino === destino).map(p => p.pedidoId);
  }
  async function productosDePedidos(ids) {
    const out = [];
    for (const pid of (ids || []).filter(Boolean)) {
      try {
        const p = await getPedidoVinculado(pid);
        if (p?.productos?.length) out.push(...p.productos);
      } catch (e) {
        console.warn("[productosDePedidos] no se pudo leer el pedido", pid, e);
      }
    }
    return out.length ? out : null;
  }

  async function handleSeleccionComplementario(pedido) {
    if (busy || !buscarComplementario) return;
    const aviso = validarDestinoPedido(pedido.productos, buscarComplementario);
    if (aviso && !window.confirm("⚠️ " + aviso)) return;
    setBusy(true);
    try {
      await vincularPedidoComplementario(obra, pedido, buscarComplementario, usuario);
      setBuscarComplementario(null);
    } catch (err) {
      alert("No se pudo vincular: " + (err.message || err));
    } finally {
      setBusy(false);
    }
  }

  // Admin: adjuntar el PDF del pedido de Finnegans y LEER sus productos (obras
  // viejas que no pasan por el sistema de pedidos) → cuenta como vinculado.
  function pedirPedidoManual(destino) {
    setPedidoManualDest(destino);
    pedidoManualRef.current?.click();
  }
  async function handlePedidoManualSeleccionado(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const destino = pedidoManualDest;
    if (!file || !destino) return;
    setLeyendoPedido(true);
    try {
      // Leer los productos del PDF (mismo parser que el remito de despacho)
      let productos = [];
      try {
        const { extraerItemsDeAdjunto } = await import("../services/parseArchivosObra");
        const url = URL.createObjectURL(file);
        const items = await extraerItemsDeAdjunto({ url, nombre: file.name }, "despacho");
        URL.revokeObjectURL(url);
        productos = items.map(it => ({ cod: it.codNum, desc: it.detalle, cant: it.cant }));
      } catch (errParse) {
        console.warn("[pedido manual] no se pudo leer", errParse);
      }
      await adjuntarPedidoManual(obra.id, destino, file, productos, usuario);
      if (productos.length === 0) {
        alert("Se adjuntó el pedido, pero no se pudieron leer los productos automáticamente. Queda adjunto igual; si necesitás el control cruzado, pasame un ejemplo del PDF para calibrar la lectura.");
      }
    } catch (err) {
      alert("No se pudo adjuntar el pedido: " + (err.message || err));
    } finally {
      setLeyendoPedido(false);
      setPedidoManualDest(null);
    }
  }

  // Corregir el cliente: se carga el maestro recién al abrir el editor.
  // Cálculo simple (no useMemo) porque acá ya pasamos el early return del componente.
  const clientesFiltradosEdit = (() => {
    const t = qClienteEdit.trim().toLowerCase();
    if (t.length < 2) return [];
    return clientesLista
      .filter(c => c.nombre.toLowerCase().includes(t) || String(c.codigo).includes(t))
      .slice(0, 8);
  })();

  async function handleCambiarCliente(c) {
    try {
      await setClienteObra(obra.id, c, usuario);
      setEditandoCliente(false);
      setQClienteEdit("");
    } catch (e) {
      alert("No se pudo cambiar el cliente: " + (e.message || e));
    }
  }

  // Pedidos de OP: la producción puede partirse en etapas (varios OP, un carpintero c/u)
  async function handleOPSeleccionado(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSubiendoOP(true);
    try {
      await agregarOP(obra.id, file, null, usuario);
    } catch (err) {
      alert("No se pudo agregar el pedido OP: " + (err.message || err));
    } finally {
      setSubiendoOP(false);
    }
  }
  async function handleEliminarOP(op) {
    if (!window.confirm(`¿Quitar el pedido OP "${op.archivo?.nombre || ""}"? Vas a tener que revisar la lectura de nuevo.`)) return;
    try {
      await eliminarOP(obra, op.id, usuario);
    } catch (err) {
      alert("No se pudo quitar: " + (err.message || err));
    }
  }

  function pedirDespachoExtra(destino) {
    setDespachoExtraDest(destino);
    despachoExtraRef.current?.click();
  }
  async function handleDespachoExtraSeleccionado(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !despachoExtraDest) return;
    setBusy(true);
    try {
      await agregarDespachoExtra(obra.id, despachoExtraDest, file, usuario);
    } catch (err) {
      alert("No se pudo adjuntar: " + (err.message || err));
    } finally {
      setBusy(false);
      setDespachoExtraDest(null);
    }
  }

  async function handleEntregada(valor) {
    const msg = valor
      ? `¿Marcar la obra ${obra.numero} como entregada?`
      : "¿Desmarcar la entrega de la obra?";
    if (!window.confirm(msg)) return;
    try {
      await marcarEntregada(obra.id, valor, usuario);
    } catch (e) {
      alert("No se pudo actualizar: " + (e.message || e));
    }
  }

  async function handleBalance(valor) {
    const msg = valor
      ? `¿Marcar el balance de la obra ${obra.numero} como hecho?\n\n(La pantalla de balance con costos y margen llega en la próxima fase.)`
      : "¿Desmarcar el balance?";
    if (!window.confirm(msg)) return;
    try {
      await marcarBalance(obra.id, valor, usuario);
    } catch (e) {
      alert("No se pudo actualizar: " + (e.message || e));
    }
  }

  /** Resumen corto de una comparación para guardar en la obra (panel Verificaciones) */
  function resumenDe(resultado) {
    const difs =
      resultado.cantidadDistinta.length +
      resultado.soloPedido.length +
      resultado.soloDocumento.length +
      (resultado.grupos || []).filter(g => !g.enPedido || !g.cantidadOk).length;
    const okCount = resultado.coinciden.length + (resultado.grupos || []).filter(g => g.enPedido && g.cantidadOk).length;
    const nSust = (resultado.sustituidos || []).length;
    if (difs > 0) return { ok: false, texto: `${difs} diferencia${difs === 1 ? "" : "s"}` };
    if (resultado.faltaGuale) return { ok: false, texto: "R8 verificado · falta el pedido GUALE" };
    return {
      ok: true,
      texto: `${okCount} coinciden${nSust ? ` · ${nSust} sustituido${nSust === 1 ? "" : "s"}` : ""}`,
    };
  }

  /**
   * Verificación del circuito: compara un documento contra los pedidos y
   * guarda el resultado. Corre AUTOMÁTICO al vincular pedidos y al adjuntar
   * despachos — el objetivo es garantizar que TODO el OP pasa por Finnegans.
   * mostrar: "siempre" (click en el panel) | "si-dif" (automático: solo
   * interrumpe cuando hay diferencias).
   */
  async function handleComparar(tipo, opts = {}) {
    const { mostrar = "siempre", archivoOverride = null, pedidoOverride = null, pedidoGualeOverride = null } = opts;
    // El OP puede ser VARIOS (producción en etapas): se suman todos.
    const esOP = tipo === "op";
    const listaOPs = esOP ? archivosOPs(obra) : [];
    const archivo = archivoOverride || (esOP ? (listaOPs[0] || null) : getArchivoObra(obra, tipo));
    const pedidoIdRef = pedidoOverride?.id || obra.pedidoId;
    if (!archivo || (!pedidoIdRef && !obra.pedidoAdjuntoR8) || comparando) return;
    setComparando(tipo);
    try {
      // El pedido del listener puede venir incompleto — buscarlo entero
      let productos = pedidoOverride?.productos?.length ? pedidoOverride.productos : pedidoVinculado?.productos;
      if (!productos?.length && pedidoIdRef) {
        const pedido = await getPedidoVinculado(pedidoIdRef);
        productos = pedido?.productos;
      }
      productos = productos?.length ? [...productos] : [];
      // Pedido adjuntado y leído a mano (admin, obras fuera del sistema)
      if (obra.pedidoAdjuntoR8?.productos?.length) productos = [...productos, ...obra.pedidoAdjuntoR8.productos];
      // Sumar los pedidos complementarios R8 (ampliaciones)
      const extraR8 = await productosDePedidos(idsComplementarios("r8"));
      if (extraR8) productos = [...productos, ...extraR8];
      if (!productos.length) throw new Error("El pedido no tiene productos cargados");

      // Segundo pedido (Gualeguay: accesorios + telas), si está vinculado
      let productosGuale = pedidoGualeOverride?.productos || null;
      const gualeId = pedidoGualeOverride?.id || obra.pedidoTelaId;
      if (!productosGuale && gualeId) {
        const pedidoGuale = await getPedidoVinculado(gualeId);
        productosGuale = pedidoGuale?.productos || null;
      }
      // Pedido GUALE adjuntado a mano
      if (obra.pedidoAdjuntoGuale?.productos?.length) productosGuale = [...(productosGuale || []), ...obra.pedidoAdjuntoGuale.productos];
      // Sumar los pedidos complementarios GUALE (ampliaciones)
      const extraGuale = await productosDePedidos(idsComplementarios("guale"));
      if (extraGuale) productosGuale = [...(productosGuale || []), ...extraGuale];

      const { extraerItemsDeAdjunto, extraerItemsDeOPs, compararItemsConPedido } =
        await import("../services/parseArchivosObra");
      const items = (esOP && !archivoOverride)
        ? await extraerItemsDeOPs(listaOPs)          // suma de TODOS los OP
        : await extraerItemsDeAdjunto(archivo, tipo);
      const resultado = compararItemsConPedido(items, productos, productosGuale, obra.sustituciones || null);

      // Persistir el resumen para el panel de verificaciones
      const resumen = resumenDe(resultado);
      guardarVerificacionObra(obra.id, tipo, resumen).catch(() => {});

      if (mostrar === "siempre" || (mostrar === "si-dif" && !resumen.ok)) {
        setComparacion({ tipo, resultado });
      }
    } catch (e) {
      if (mostrar === "siempre") alert("No se pudo verificar: " + (e.message || e));
      else console.warn("[verificación automática]", e);
    } finally {
      setComparando(null);
    }
  }

  /** Marca que un producto de Finnegans reemplaza intencionalmente a uno del OP */
  async function marcarSustitucion(claveOP, prod) {
    const mapa = { ...(obra.sustituciones || {}) };
    mapa[claveOP] = {
      cod: String(prod.cod),
      desc: prod.desc || "",
      porNombre: usuario.nombre || usuario.displayName || usuario.email || null,
    };
    try {
      await setSustitucionesObra(obra.id, mapa, usuario, `${prod.cod} reemplaza a ${claveOP}`);
      handleComparar("op", { mostrar: "nunca" }); // refrescar la verificación guardada
    } catch (e) {
      alert("No se pudo guardar el reemplazo: " + (e.message || e));
    }
  }

  async function quitarSustitucion(claveOP) {
    const mapa = { ...(obra.sustituciones || {}) };
    delete mapa[claveOP];
    try {
      await setSustitucionesObra(obra.id, mapa, usuario, `Se quitó el reemplazo de ${claveOP}`);
      handleComparar("op", { mostrar: "nunca" });
    } catch (e) {
      alert("No se pudo quitar el reemplazo: " + (e.message || e));
    }
  }

  async function handleConfirmarOP(cont) {
    setConfirmandoOP(true);
    try {
      await confirmarLecturaOP(obra.id, cont || null, usuario);
      setShowConfirmarOP(false);
    } catch (e) {
      alert("No se pudo confirmar: " + (e.message || e));
    } finally {
      setConfirmandoOP(false);
    }
  }

  async function handleAgregarFacturaTipo(tipo, e) {
    e.preventDefault();
    try {
      await agregarFactura(obra.id, { ...facturaForm[tipo], tipo }, facturaFileByTipo[tipo], usuario);
      setFacturaForm(f => ({ ...f, [tipo]: { monto: "", nota: "" } }));
      setFacturaFileByTipo(f => ({ ...f, [tipo]: null }));
    } catch (err) {
      alert(err.message || err);
    }
  }

  /** Abre el editor de "a nombre de", precargando el cliente de la obra */
  function abrirEditorNombreFactura() {
    const actual = obra.facturaANombreDe?.nombre
      ? obra.facturaANombreDe
      : (obra.cliente ? { nombre: obra.cliente, codigo: obra.clienteCodigo || null } : null);
    setNombreFactura(actual);
    setQNombreFactura("");
    setEditandoNombreFactura(true);
  }
  async function handleGuardarNombreFactura() {
    try {
      await setFacturaANombreDe(obra.id, nombreFactura, usuario);
      setEditandoNombreFactura(false);
      setQNombreFactura("");
    } catch (err) {
      alert(err.message || err);
    }
  }

  /** Abre el editor del monto del presupuesto (carga a mano) */
  function abrirEditorMonto() {
    setMontoInput(obra.montoPresupuesto ? String(obra.montoPresupuesto) : "");
    setMonedaInput(obra.monedaPresupuesto || "USD");
    setEditandoMonto(true);
  }
  async function handleGuardarMonto() {
    const monto = Number(montoInput);
    if (!(monto > 0)) { alert("Ingresá un monto válido"); return; }
    try {
      await actualizarMontoPresupuesto(
        obra.id, { monto, moneda: monedaInput }, usuario,
        `Monto del presupuesto cargado a mano — ${monedaInput} ${monto}`,
      );
      setEditandoMonto(false);
    } catch (err) {
      alert(err.message || err);
    }
  }

  async function handleHabilitarAnticipada(e) {
    e.preventDefault();
    try {
      await habilitarFacturacionAnticipada(obra.id, motivoAnticipada.trim(), usuario);
      setPidiendoAnticipada(false);
      setMotivoAnticipada("");
    } catch (err) {
      alert(err.message || err);
    }
  }

  async function handleAgregarCompra(e) {
    e.preventDefault();
    try {
      await agregarCompraPlaza(obra.id, nuevaCompra, compraFile, usuario);
      setNuevaCompra({ nota: "", monto: "" });
      setCompraFile(null);
    } catch (err) {
      alert(err.message || err);
    }
  }

  // Actualización de presupuesto → cambio de OP: generar pedido complementario
  function handleGenerarComplementario(deltaItems) {
    setShowCambiosOP(false);
    setExportDelta(deltaItems);
    setShowExport(true);
  }

  async function handleMarcarRevisadoOP() {
    try {
      await descartarCambioOP(obra.id, usuario);
      setShowCambiosOP(false);
    } catch (err) {
      alert(err.message || err);
    }
  }

  async function handleAgregarCobranza(e) {
    e.preventDefault();
    try {
      await agregarCobranza(obra.id, nuevaCobranza, usuario);
      setNuevaCobranza({ monto: "", fecha: "", nota: "", efectivo: false });
      setMostrarFormCobranza(false);
    } catch (err) {
      alert(err.message || err);
    }
  }

  function abrirEdicionCobranza(c) {
    // La fecha guardada es Timestamp → la paso a yyyy-mm-dd para el input date
    let fechaStr = "";
    const secs = c.fecha?.seconds;
    if (secs) fechaStr = new Date(secs * 1000).toISOString().slice(0, 10);
    setEditCobranza({ monto: c.monto ?? "", fecha: fechaStr, nota: c.nota || "", efectivo: !!c.efectivo });
    setEditandoCobranza(c.id);
    setMostrarFormCobranza(false);
  }
  async function handleEditarCobranza(e) {
    e.preventDefault();
    try {
      await editarCobranza(obra.id, editandoCobranza, editCobranza, usuario);
      setEditandoCobranza(null);
    } catch (err) {
      alert(err.message || err);
    }
  }

  function pedirDocCobranza(cobranzaId, campo) {
    cobranzaTargetRef.current = { id: cobranzaId, campo };
    cobranzaFileRef.current?.click();
  }

  async function handleDocCobranza(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    const target = cobranzaTargetRef.current;
    if (!file || !target) return;
    try {
      await adjuntarDocCobranza(obra.id, target.id, target.campo, file, usuario);
    } catch (err) {
      alert("No se pudo adjuntar: " + (err.message || err));
    }
  }

  async function handleAgregarGasto(e) {
    e.preventDefault();
    try {
      await agregarGastoObra(obra.id, nuevoGasto, usuario);
      setNuevoGasto({ concepto: "", monto: "" });
    } catch (err) {
      alert(err.message || err);
    }
  }

  async function handleGuardarNotas() {
    try {
      await actualizarObra(obra.id, { notas: notasDraft });
      setNotasDraft(null);
    } catch (e) {
      alert("No se pudieron guardar las notas: " + (e.message || e));
    }
  }

  async function handleEliminar() {
    const confirmacion = window.prompt(
      `⚠️ Vas a ELIMINAR la obra ${obra.numero}${obra.cliente ? ` (${obra.cliente})` : ""} con su historial y archivos adjuntos.\n` +
      "Los pedidos de Finnegans vinculados NO se borran.\n\n" +
      `Para confirmar, escribí el número de la obra (${obra.numero}):`
    );
    if (confirmacion === null) return;
    if (String(confirmacion).trim() !== String(obra.numero)) {
      alert("El número no coincide — no se eliminó nada.");
      return;
    }
    setBusy(true);
    try {
      await eliminarObra(obra.id);
      navigate("/obras");
    } catch (e) {
      alert("No se pudo eliminar: " + (e.message || e));
      setBusy(false);
    }
  }

  // Fila de documento adjuntable dentro del progreso
  function docDetalle(tipo, habilitado, hintBloqueo, extra) {
    const archivo = getArchivoObra(obra, tipo);
    if (archivo) {
      return (
        <>
          <a href={archivo.url} target="_blank" rel="noreferrer">{archivo.nombre}</a>
          {extra ? <span style={{ color: "#94a3b8" }}> · {extra}</span> : null}
          <span style={{ color: "#94a3b8" }}> · <Firma marca={archivo} /></span>
        </>
      );
    }
    return <span style={{ color: habilitado ? "#c2410c" : "#94a3b8" }}>{hintBloqueo}</span>;
  }

  // Fila de documento con la estructura uniforme de toda la ficha
  function filaDoc(tipo, label, habilitado, hint, opts = {}) {
    return (
      <FilaArchivo
        etiqueta={label}
        cuerpo={docDetalle(tipo, habilitado, hint, opts.extra)}
        acciones={
          <>
            {opts.antesDeAcciones}
            {docAccion(tipo, habilitado)}
          </>
        }
        ultimo={opts.ultimo}
      />
    );
  }

  // Admin: marcar un paso como "cargado (pendiente de archivo)" para dar de alta
  // obras ya avanzadas sin tener todavía el documento. `realOk` = ya hay archivo real.
  function botonHitoManual(hito, realOk) {
    if (!permisos.admin) return null;
    if (realOk) return null; // ya está cargado de verdad, no tiene sentido marcarlo
    const manual = esHitoManual(obra, hito);
    if (manual) {
      return (
        <button className="btn btn--ghost" style={{ fontSize: 11, color: "#b45309" }}
          onClick={() => marcarHitoManual(obra.id, hito, false, usuario)}>
          ⏳ pendiente de archivo · quitar
        </button>
      );
    }
    return (
      <button className="btn btn--ghost" style={{ fontSize: 11 }}
        onClick={() => marcarHitoManual(obra.id, hito, true, usuario)}>
        Admin: marcar cargado
      </button>
    );
  }

  async function handleEliminarArchivo(tipo) {
    if (!window.confirm("¿Eliminar este archivo adjunto? Vas a poder adjuntar otro en su lugar.")) return;
    try {
      await eliminarArchivoObra(obra.id, tipo, usuario, getArchivoObra(obra, tipo)?.path);
    } catch (e) {
      alert("No se pudo eliminar: " + (e.message || e));
    }
  }

  function docAccion(tipo, habilitado) {
    if (!puedeEditar) return null;
    // Compra de accesorios y despacho R8: SOLO Marcela (obrasDespacho) o admin
    if (["despacho", "facturaCompra", "recepcionCompra"].includes(tipo) && !permisos.despacho) return null;
    const archivo = getArchivoObra(obra, tipo);
    const subiendo = subiendoDoc === tipo;
    if (!archivo && !habilitado) return null;
    return (
      <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
        <button
          className={archivo ? "btn btn--ghost" : "btn btn--outline"}
          style={{ fontSize: 12 }}
          disabled={!!subiendoDoc}
          onClick={() => pedirArchivo(tipo)}
        >
          {subiendo ? "Subiendo…" : archivo ? "Reemplazar" : "Adjuntar"}
        </button>
        {archivo && !subiendo && (
          <button
            className="btn btn--ghost"
            title="Eliminar archivo"
            style={{ fontSize: 12.5, color: "#b91c1c", padding: "0 7px" }}
            disabled={!!subiendoDoc}
            onClick={() => handleEliminarArchivo(tipo)}
          >
            ✕
          </button>
        )}
      </span>
    );
  }

  return (
    <div className="obras-ficha" style={{ padding: 16, maxWidth: 1280, margin: "0 auto" }}>
      <button className="btn btn--ghost" onClick={() => navigate("/obras")} style={{ marginBottom: 10 }}>
        ← Obras
      </button>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={handleArchivoSeleccionado}
      />
      <input
        ref={despachoExtraRef}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
        style={{ display: "none" }}
        onChange={handleDespachoExtraSeleccionado}
      />
      <input
        ref={pedidoManualRef}
        type="file"
        accept=".pdf"
        style={{ display: "none" }}
        onChange={handlePedidoManualSeleccionado}
      />
      <input
        ref={opFileRef}
        type="file"
        accept=".doc,.docx,.pdf,.xls,.xlsx"
        style={{ display: "none" }}
        onChange={handleOPSeleccionado}
      />

      {/* ── Cabecera ── */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 20 }}>
              Obra {obra.numero}{obra.cliente ? ` · ${obra.cliente}` : ""}
            </h1>
            {obra.nombreObra && <div style={{ color: "#64748b", fontSize: 14, marginTop: 2 }}>{obra.nombreObra}</div>}
          </div>
          <span style={{
            fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
            background: fase.headerBg, color: fase.color, padding: "5px 12px", borderRadius: 999,
          }}>
            {fase.icon} {fase.label}
          </span>
        </div>

        <div style={{ display: "flex", gap: 10, marginTop: 12, alignItems: "center" }}>
          <div style={{ flex: 1 }}><ProgresoBar obra={obra} alto={7} /></div>
          <span style={{ fontSize: 12, color: "#64748b", whiteSpace: "nowrap", fontWeight: 600 }}>
            {progresoObra(obra).completos} / {progresoObra(obra).total}
          </span>
        </div>
      </div>

      {/* ── Te toca ── */}
      {misAcciones.length > 0 && (
        <div style={{
          ...cardStyle,
          background: "#fffbeb", border: "1px solid #fde68a",
        }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>
            Acciones pendientes a tu cargo
          </div>
          {misAcciones.map(a => (
            <div key={a.hito} style={{ fontSize: 13, color: "#92400e", padding: "2px 0" }}>
              • {a.label}
            </div>
          ))}
        </div>
      )}

      {/* ── Cambio de OP pendiente de revisar (tras actualizar presupuesto) ── */}
      {obra.opCambioPendiente && puedeEditar && (
        <div style={{ ...cardStyle, background: "#fff7ed", border: "1px solid #fed7aa", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ fontSize: 13, color: "#9a3412" }}>
            <b>Se actualizó el OP.</b> Revisá qué cambió respecto al pedido anterior para decidir si hay que hacer un pedido complementario.
          </div>
          <button className="btn" style={{ fontSize: 12.5, background: "#ea580c", color: "#fff", border: "none", whiteSpace: "nowrap" }}
            onClick={() => setShowCambiosOP(true)}>
            Ver cambios del OP
          </button>
        </div>
      )}

      {/* ── Layout: proceso (izquierda) + resumen (derecha) ── */}
      <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 1fr) 320px" : "1fr", gap: 16, alignItems: "start" }}>
      <div style={{ minWidth: 0 }}>

      {/* ── Progreso de la obra ── */}
      <div style={{ marginBottom: 12 }}>
        <p style={{ fontSize: 11.5, color: "#94a3b8", margin: "0 0 8px" }}>
          Presupuesto → pedidos de venta → despachos → producción y entrega → facturación.
          Cobranza y balance van en paralelo. Cada documento queda verificado contra el OP.
        </p>

        {/* 1 · Presupuesto (monto que paga el cliente) */}
        <Paso
          numero={1}
          titulo={<>Presupuesto{meToca("presupuesto") && pillTeToca}</>}
          completo={hitos.presupuesto}
          activo={!hitos.presupuesto}
          estado={hitos.presupuesto
            ? (obra.montoPresupuesto
                ? <EstadoPaso tono="ok" texto={`${obra.monedaPresupuesto || ""} ${Number(obra.montoPresupuesto).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`} />
                : <EstadoPaso tono="aviso" texto="Sin monto" />)
            : <EstadoPaso tono="activo" texto="Pendiente" />}
          detalle={
            <SeccionDocs>
              {filaDoc("presupuesto", "Presupuesto aceptado", true, "adjuntar el presupuesto aceptado por el cliente", {
                extra: obra.presupuestoActualizadoAt ? `actualizado ${formatFechaHora(obra.presupuestoActualizadoAt)}` : null,
                ultimo: true,
              })}
              {/* Monto del presupuesto — se carga a mano */}
              {editandoMonto ? (
                <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 8, flexWrap: "wrap" }}>
                  <select
                    style={{ width: 78, fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }}
                    value={monedaInput} onChange={e => setMonedaInput(e.target.value)}
                  >
                    <option value="USD">USD</option>
                    <option value="UYU">UYU</option>
                    <option value="EUR">EUR</option>
                  </select>
                  <input
                    style={{ width: 130, fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} type="number" step="0.01" min="0" autoFocus
                    value={montoInput} placeholder="Monto (sin IVA)"
                    onChange={e => setMontoInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleGuardarMonto(); }}
                  />
                  <button type="button" className="btn btn--primary" style={{ fontSize: 12 }} onClick={handleGuardarMonto}>Guardar</button>
                  <button type="button" className="btn btn--ghost" style={{ fontSize: 12 }} onClick={() => setEditandoMonto(false)}>Cancelar</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center", paddingTop: 8 }}>
                  <span style={{ fontSize: 12.5, color: obra.montoPresupuesto ? "#334155" : "#c2410c" }}>
                    {obra.montoPresupuesto
                      ? `Monto: ${obra.monedaPresupuesto || ""} ${Number(obra.montoPresupuesto).toLocaleString("es-UY", { minimumFractionDigits: 2 })} (sin IVA)`
                      : "Falta cargar el monto del presupuesto"}
                  </span>
                  <button type="button" className="btn btn--ghost" style={{ fontSize: 12 }} onClick={abrirEditorMonto}>
                    {obra.montoPresupuesto ? "✎ monto" : "Cargar monto"}
                  </button>
                </div>
              )}
            </SeccionDocs>
          }
        />

        {/* 2 · Pedidos de venta — verde SOLO si los pedidos coinciden con el OP */}
        {(() => {
          const verifOP = obra.verificaciones?.op;
          const opConfirmado = !!obra.opConfirmado;
          const verificado = hitos.pedido && verifOP?.ok === true;
          const faltaConfirmar = hitos.op && !opConfirmado;
          const conAviso = (hitos.pedido && !verificado) || faltaConfirmar;
          return (
            <Paso
              numero={2}
              titulo={<>Pedidos de venta{(meToca("op") || meToca("vinculado")) && pillTeToca}</>}
              completo={verificado}
              advertencia={conAviso}
              activo={!hitos.pedido}
              estado={
                verificado ? <EstadoPaso tono="ok" texto="Verificado con el OP" />
                : faltaConfirmar ? <EstadoPaso tono="aviso" texto="Falta confirmar el OP" />
                : verifOP?.ok === false ? <EstadoPaso tono="aviso" texto={`No coincide · ${verifOP.texto}`} />
                : hitos.pedido ? <EstadoPaso tono="aviso" texto="Sin verificar" />
                : <EstadoPaso tono="activo" texto="En curso" />
              }
              detalle={
                <>
                {/* ── Pedidos de OP — la producción puede hacerse en etapas (varios
                    OP, un carpintero c/u). Sus productos se suman para los pedidos. ── */}
                <SeccionDocs titulo="PEDIDOS DE OP">
                  {(() => {
                    const ops = opsDeObra(obra);
                    const botonMas = puedeEditar && (
                      <button className="btn btn--ghost" title="Agregar otro pedido de OP"
                        style={{ fontSize: 14, padding: "0 8px", lineHeight: 1.4 }}
                        disabled={subiendoOP} onClick={() => opFileRef.current?.click()}>
                        {subiendoOP ? "…" : "+"}
                      </button>
                    );
                    if (ops.length === 0) {
                      return (
                        <FilaArchivo
                          ultimo
                          etiqueta="Pedido OP"
                          cuerpo={esHitoManual(obra, "op")
                            ? <span style={{ color: "#b45309" }}>obra vieja — sin OP en el sistema (marcado por admin)</span>
                            : <span style={{ color: "#c2410c" }}>adjuntar el archivo exportado de OP</span>}
                          acciones={puedeEditar && (
                            <>
                              <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={subiendoOP}
                                onClick={() => opFileRef.current?.click()}>
                                {subiendoOP ? "Subiendo…" : "Adjuntar"}
                              </button>
                              {botonHitoManual("op", false)}
                            </>
                          )}
                        />
                      );
                    }
                    return ops.map((op, i) => (
                      <FilaArchivo
                        key={op.id}
                        ultimo={i === ops.length - 1}
                        etiqueta={ops.length > 1 ? `Pedido OP ${i + 1} de ${ops.length}` : "Pedido OP"}
                        cuerpo={
                          <>
                            <a href={op.archivo?.url} target="_blank" rel="noreferrer">{op.archivo?.nombre}</a>
                            {op.por ? <span style={{ color: "#94a3b8" }}> · {op.por}</span> : null}
                          </>
                        }
                        acciones={puedeEditar && (
                          <>
                            <select
                              value={op.carpintero || ""}
                              onChange={e => setCarpinteroOP(obra, op.id, e.target.value, usuario)}
                              title="Carpintero responsable de esta etapa"
                              style={{ fontSize: 11.5, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1" }}
                            >
                              <option value="">Carpintero…</option>
                              {CARPINTEROS.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                            {i === ops.length - 1 && botonMas}
                            <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px", color: "#b91c1c" }}
                              onClick={() => handleEliminarOP(op)}>✕</button>
                          </>
                        )}
                      />
                    ));
                  })()}
                </SeccionDocs>

                {/* ── Pedidos de venta en Finnegans (R8 y Gualeguay) ── */}
                <div style={{ marginTop: 12 }}>
                <SeccionDocs titulo="PEDIDOS DE FINNEGANS">
                  {/* Pedido R8 — vinculado del sistema o adjuntado y leído */}
                  <FilaArchivo
                    etiqueta="Pedido R8 (perfiles, refuerzos, vidrios)"
                    cuerpo={
                      pedidoVinculado ? (
                        <>
                          <a onClick={e => { e.preventDefault(); verProductosDelPedido(pedidoVinculado.id, pedidoVinculado); }}
                            href={`/pedidos/${encodeURIComponent(pedidoVinculado.id)}`}
                            title="Ver los productos del pedido">{pedidoVinculado.id}</a>
                          <span style={{ color: "#94a3b8" }}>
                            {pedidoVinculado.productos?.length ? ` · ${pedidoVinculado.productos.length} productos` : ""} · vinculado
                          </span>
                        </>
                      ) : obra.pedidoAdjuntoR8 ? (
                        <>
                          <a href={obra.pedidoAdjuntoR8.archivo?.url} target="_blank" rel="noreferrer">{obra.pedidoAdjuntoR8.archivo?.nombre}</a>
                          <span style={{ color: obra.pedidoAdjuntoR8.productos?.length ? "#94a3b8" : "#c2410c" }}>
                            {obra.pedidoAdjuntoR8.productos?.length
                              ? ` · ${obra.pedidoAdjuntoR8.productos.length} productos leídos`
                              : " · sin productos leídos"}
                          </span>
                        </>
                      ) : esHitoManual(obra, "vinculado") ? (
                        <span style={{ color: "#b45309" }}>obra vieja — sin pedido en Finnegans (marcado por admin)</span>
                      ) : !hitos.op ? (
                        <span style={{ color: "#94a3b8" }}>primero el pedido OP</span>
                      ) : (
                        <span style={{ color: "#c2410c" }}>generar import → importar en Finnegans → vincular</span>
                      )
                    }
                    acciones={
                      puedeEditar && (
                        pedidoVinculado ? (
                          <button className="btn btn--ghost" style={{ fontSize: 12 }}
                            onClick={() => window.confirm(hitos.despacho
                              ? "⚠️ Esta obra ya tiene despacho adjunto. ¿Desvincular el pedido igual?"
                              : "¿Desvincular el pedido de esta obra?") && desvincularPedidoDeObra(obra.id, usuario)}>
                            Desvincular
                          </button>
                        ) : obra.pedidoAdjuntoR8 ? (
                          permisos.admin && (
                            <button className="btn btn--ghost" style={{ fontSize: 12, color: "#b91c1c" }}
                              onClick={() => window.confirm("¿Quitar el pedido adjunto?") && eliminarPedidoManual(obra.id, "r8", usuario, obra.pedidoAdjuntoR8.archivo?.path)}>
                              Quitar
                            </button>
                          )
                        ) : (
                          <>
                            {hitos.op && (
                              <>
                                <button className="btn btn--ghost" style={{ fontSize: 12 }} disabled={busy} onClick={() => setShowBuscar(true)}>Vincular</button>
                                {permisos.admin && (
                                  <button className="btn btn--ghost" style={{ fontSize: 12 }} disabled={leyendoPedido}
                                    title="Admin: adjuntar el PDF del pedido de Finnegans y leer sus productos"
                                    onClick={() => pedirPedidoManual("r8")}>
                                    {leyendoPedido && pedidoManualDest === "r8" ? "Leyendo…" : "Adjuntar"}
                                  </button>
                                )}
                              </>
                            )}
                            {botonHitoManual("vinculado", !!obra.pedidoId || !!obra.pedidoAdjuntoR8)}
                          </>
                        )
                      )
                    }
                  />

                  {/* Pedido Gualeguay */}
                  <FilaArchivo
                    ultimo
                    etiqueta="Pedido Gualeguay (accesorios + telas)"
                    cuerpo={
                      obra.pedidoTelaId ? (
                        <>
                          <a onClick={e => { e.preventDefault(); verProductosDelPedido(obra.pedidoTelaId, candidatos.find(p => p.id === obra.pedidoTelaId)); }}
                            href={`/pedidos/${encodeURIComponent(obra.pedidoTelaId)}`}
                            title="Ver los productos del pedido">{obra.pedidoTelaId}</a>
                          <span style={{ color: "#94a3b8" }}> · vinculado</span>
                        </>
                      ) : obra.pedidoAdjuntoGuale ? (
                        <>
                          <a href={obra.pedidoAdjuntoGuale.archivo?.url} target="_blank" rel="noreferrer">{obra.pedidoAdjuntoGuale.archivo?.nombre}</a>
                          <span style={{ color: obra.pedidoAdjuntoGuale.productos?.length ? "#94a3b8" : "#c2410c" }}>
                            {obra.pedidoAdjuntoGuale.productos?.length
                              ? ` · ${obra.pedidoAdjuntoGuale.productos.length} productos leídos`
                              : " · sin productos leídos"}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>solo si la obra lleva material de Gualeguay</span>
                      )
                    }
                    acciones={
                      puedeEditar && (
                        obra.pedidoTelaId ? (
                          <button className="btn btn--ghost" style={{ fontSize: 12 }}
                            onClick={() => window.confirm("¿Desvincular el pedido de Gualeguay?") && desvincularPedidoTelaDeObra(obra.id, usuario)}>
                            Desvincular
                          </button>
                        ) : obra.pedidoAdjuntoGuale ? (
                          permisos.admin && (
                            <button className="btn btn--ghost" style={{ fontSize: 12, color: "#b91c1c" }}
                              onClick={() => window.confirm("¿Quitar el pedido adjunto?") && eliminarPedidoManual(obra.id, "guale", usuario, obra.pedidoAdjuntoGuale.archivo?.path)}>
                              Quitar
                            </button>
                          )
                        ) : hitos.op ? (
                          <>
                            <button className="btn btn--ghost" style={{ fontSize: 12 }} disabled={busy} onClick={() => setShowBuscarTela(true)}>Vincular</button>
                            {permisos.admin && (
                              <button className="btn btn--ghost" style={{ fontSize: 12 }} disabled={leyendoPedido}
                                onClick={() => pedirPedidoManual("guale")}>
                                {leyendoPedido && pedidoManualDest === "guale" ? "Leyendo…" : "Adjuntar"}
                              </button>
                            )}
                          </>
                        ) : null
                      )
                    }
                  />

                </SeccionDocs>
                </div>

                {/* Acciones del paso */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 12 }}>
                  {puedeEditar && hitos.op && !opConfirmado && (
                    <button className="btn" style={{ fontSize: 12, background: "#f59e0b", color: "#fff", border: "none" }}
                      onClick={() => setShowConfirmarOP(true)}>
                      Confirmar lectura del OP
                    </button>
                  )}
                  {puedeEditar && hitos.op && (
                    <button className="btn btn--principal" style={{ fontSize: 12 }} disabled={busy} onClick={() => setShowExport(true)}>
                      Generar import
                    </button>
                  )}
                  {hitos.op && (
                    <button className="btn btn--outline" style={{ fontSize: 12 }} onClick={() => setDetalleGrupo("pedidos")}>
                      Ver productos
                    </button>
                  )}
                </div>
                </>
              }
            />
          );
        })()}

        {/* 3 · Compra de accesorios (no se stockean: solo se documenta la compra) */}
        <Paso
          numero={3}
          titulo={<>Compra de accesorios{meToca("facturaCompra") && pillTeToca}</>}
          completo={!conGuale || hitos.facturaCompra}
          activo={conGuale && hitoHabilitado(obra, "facturaCompra") && !hitos.facturaCompra}
          bloqueado={conGuale && !hitoHabilitado(obra, "facturaCompra")}
          estado={
            !conGuale ? <EstadoPaso texto="No aplica" />
            : hitos.facturaCompra ? <EstadoPaso tono="ok" texto="Compra cargada" />
            : hitoHabilitado(obra, "facturaCompra") ? <EstadoPaso tono="activo" texto="Falta cargar la compra" />
            : <EstadoPaso texto="Confirmá el OP primero" />
          }
          detalle={
            !conGuale ? null : (
              <SeccionDocs>
                <div style={{ fontSize: 11.5, color: "#94a3b8", marginBottom: 4 }}>
                  Los accesorios no se stockean ni se despachan. Alcanza con documentar la compra:
                  la factura de Finnegans y/o los comprobantes de plaza.
                </div>

                {/* Factura de compra de Finnegans */}
                {filaDoc("facturaCompraFinnegans", "Factura de compra de Finnegans", hitoHabilitado(obra, "facturaCompra"),
                  hitoHabilitado(obra, "facturaCompra") ? "los accesorios comprados por Finnegans" : "confirmá el OP primero")}

                {/* Comprobantes de compra en plaza — varios (distintos lugares) */}
                <FilaArchivo
                  etiqueta="Comprobantes de compra en plaza"
                  cuerpo={
                    comprasPlaza.length > 0 ? (
                      <span style={{ color: "#94a3b8" }}>{comprasPlaza.length} comprobante{comprasPlaza.length === 1 ? "" : "s"}</span>
                    ) : esHitoManual(obra, "facturaCompra") ? (
                      <span style={{ color: "#b45309" }}>marcado como cargado (pendiente de archivo)</span>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>
                        {hitoHabilitado(obra, "facturaCompra") ? "opcional — si se compró algo en plaza" : "confirmá el OP primero"}
                      </span>
                    )
                  }
                  acciones={botonHitoManual("facturaCompra", comprasPlaza.length > 0 || !!getArchivoObra(obra, "facturaCompraFinnegans"))}
                />
                {comprasPlaza.map(c => (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 0 4px 14px", borderBottom: "1px solid #f8fafc" }}>
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      {c.archivo ? <a href={c.archivo.url} target="_blank" rel="noreferrer">{c.archivo.nombre}</a> : "—"}
                      {c.monto ? <span style={{ fontWeight: 600 }}> · {obra.monedaPresupuesto || "$"} {Number(c.monto).toLocaleString("es-UY", { minimumFractionDigits: 2 })}</span> : null}
                      {c.nota ? <span style={{ color: "#94a3b8" }}> · {c.nota}</span> : null}
                      <span style={{ color: "#94a3b8" }}> · <Firma marca={c} /></span>
                    </span>
                    {puedeEditar && (
                      <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px", color: "#b91c1c", flexShrink: 0 }}
                        onClick={() => window.confirm("¿Eliminar este comprobante?") && eliminarCompraPlaza(obra.id, c.id)}>✕</button>
                    )}
                  </div>
                ))}
                {puedeEditar && hitoHabilitado(obra, "facturaCompra") && (
                  <form onSubmit={handleAgregarCompra} style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "6px 0 8px 14px", borderBottom: "1px solid #f1f5f9" }}>
                    <input value={nuevaCompra.nota}
                      onChange={e => setNuevaCompra(f => ({ ...f, nota: e.target.value }))}
                      placeholder="Lugar / proveedor" style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1", width: 140 }} />
                    <input type="number" step="0.01" min="0" value={nuevaCompra.monto}
                      onChange={e => setNuevaCompra(f => ({ ...f, monto: e.target.value }))}
                      placeholder="Monto (opc.)" style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1", width: 110 }} />
                    <input ref={compraFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx" style={{ display: "none" }} onChange={e => setCompraFile(e.target.files?.[0] || null)} />
                    <button type="button" className="btn btn--outline" style={{ fontSize: 12 }} onClick={() => compraFileRef.current?.click()}>
                      {compraFile ? `✓ ${compraFile.name.slice(0, 14)}…` : "Comprobante"}
                    </button>
                    <button type="submit" className="btn btn--outline" style={{ fontSize: 12 }} disabled={!compraFile}>Agregar</button>
                  </form>
                )}

              </SeccionDocs>
            )
          }
        />

        {/* 4 · Despachos (R8 y Guale) */}
        <Paso
          numero={4}
          titulo={<>Despachos (R8 y Gualeguay)
            {(meToca("despacho") || meToca("despachoGuale")) && pillTeToca}
          </>}
          completo={hitos.despacho && (!conGuale || hitos.despachoGuale)}
          activo={hitoHabilitado(obra, "despacho") && !(hitos.despacho && (!conGuale || hitos.despachoGuale))}
          bloqueado={!hitoHabilitado(obra, "despacho")}
          estado={
            hitos.despacho && (!conGuale || hitos.despachoGuale) ? <EstadoPaso tono="ok" texto="Despachado" />
            : !hitoHabilitado(obra, "despacho") ? <EstadoPaso texto="Confirmá el OP primero" />
            : <EstadoPaso tono="aviso" texto="Falta remito" />
          }
          detalle={
            <SeccionDocs>
              {filaDoc("despacho", `Remito R8${obra.carpintero ? ` · ${obra.carpintero}` : ""}`, hitoHabilitado(obra, "despacho"),
                esHitoManual(obra, "despacho") ? "pendiente de subir el remito" : hitoHabilitado(obra, "despacho") ? "perfiles y refuerzos al carpintero" : "confirmá el OP primero",
                { antesDeAcciones: botonHitoManual("despacho", !!getArchivoObra(obra, "despacho")) })}

              {conGuale ? (
                filaDoc("despachoGuale", "Remito Gualeguay", hitoHabilitado(obra, "despachoGuale"),
                  esHitoManual(obra, "despachoGuale") ? "pendiente de subir el remito" : hitoHabilitado(obra, "despachoGuale") ? "telas mosquiteras de Gualeguay" : "confirmá el OP primero",
                  { ultimo: (obra.despachosExtra || []).length === 0, antesDeAcciones: botonHitoManual("despachoGuale", !!getArchivoObra(obra, "despachoGuale")) })
              ) : (
                <FilaArchivo
                  ultimo={(obra.despachosExtra || []).length === 0}
                  etiqueta="Remito Gualeguay"
                  cuerpo={<span style={{ color: "#94a3b8" }}>no aplica — sin pedido de Gualeguay</span>}
                />
              )}

              {/* Remitos adicionales (ampliaciones / segundas rondas) */}
              {(obra.despachosExtra || []).map((d, i, arr) => (
                <FilaArchivo
                  key={d.id}
                  ultimo={i === arr.length - 1}
                  etiqueta={`Remito adicional ${d.destino === "r8" ? "R8" : "Gualeguay"}`}
                  cuerpo={
                    <>
                      {d.archivo ? <a href={d.archivo.url} target="_blank" rel="noreferrer">{d.archivo.nombre}</a> : "—"}
                      {d.por ? <span style={{ color: "#94a3b8" }}> · {d.por}</span> : null}
                    </>
                  }
                  acciones={permisos.despacho && (
                    <>
                      <select
                        value={d.carpintero || ""}
                        onChange={e => setCarpinteroDespachoExtra(obra, d.id, e.target.value, usuario)}
                        title="Etapa / carpintero de este remito"
                        style={{ fontSize: 11.5, padding: "3px 6px", borderRadius: 6, border: "1px solid #cbd5e1" }}
                      >
                        <option value="">Etapa…</option>
                        {CARPINTEROS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px", color: "#b91c1c" }}
                        onClick={() => window.confirm("¿Eliminar este remito adicional?") && eliminarDespachoExtra(obra, d.id, usuario)}>✕</button>
                    </>
                  )}
                />
              ))}

              {/* Acciones del paso */}
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                {permisos.despacho && hitoHabilitado(obra, "despacho") && (
                  <>
                    <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => pedirDespachoExtra("r8")}>Remito R8 adicional</button>
                    {conGuale && (
                      <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => pedirDespachoExtra("guale")}>Remito GUALE adicional</button>
                    )}
                  </>
                )}
              </div>
            </SeccionDocs>
          }
          accion={
            (hitos.despacho || hitos.despachoGuale) && (
              <button className="btn btn--outline" style={{ fontSize: 12, whiteSpace: "nowrap" }} onClick={() => setDetalleGrupo("despachos")}>
                Ver productos
              </button>
            )
          }
        />

        {/* 5 · Entrega */}
        <Paso
          numero={5}
          titulo={<>Entrega{meToca("entregada") && pillTeToca}</>}
          completo={hitos.entregada}
          activo={hitoHabilitado(obra, "entregada") && !hitos.entregada}
          bloqueado={!hitoHabilitado(obra, "entregada")}
          estado={
            hitos.entregada ? <EstadoPaso tono="ok" texto="Entregada" />
            : hitoHabilitado(obra, "entregada") ? <EstadoPaso tono="activo" texto="En carpintería" />
            : <EstadoPaso texto="Requiere los despachos" />
          }
          detalle={
            hitos.entregada ? <span style={{ color: "#64748b" }}>Confirmada por <Firma marca={obra.entregada} /></span>
              : hitoHabilitado(obra, "entregada")
                ? <span style={{ color: "#64748b" }}>Confirmar cuando se entregue al cliente.</span>
                : null
          }
          accion={
            puedeEditar && hitoHabilitado(obra, "entregada") && (
              hitos.entregada ? (
                <button className="btn btn--ghost" style={{ fontSize: 12 }} onClick={() => handleEntregada(false)}>
                  Deshacer
                </button>
              ) : (
                <button className="btn btn--outline" style={{ fontSize: 12 }} onClick={() => handleEntregada(true)}>
                  Confirmar entrega
                </button>
              )
            )
          }
        />


        {finalizada && (
          <div style={{
            marginTop: 10, padding: "10px 12px", borderRadius: 8,
            background: "#f0fdf4", color: "#15803d", fontSize: 13.5, fontWeight: 600,
          }}>
            ✅ Obra finalizada — legajo completo
          </div>
        )}
      </div>

      {showBuscar && (
        <BuscarPedidoModal
          obraNumero={obra.numero}
          destino="r8"
          busy={busy}
          excluirIds={new Set([obra.pedidoTelaId].filter(Boolean))}
          onSelect={handleSeleccionPedido}
          onClose={() => setShowBuscar(false)}
        />
      )}

      {showBuscarTela && (
        <BuscarPedidoModal
          obraNumero={obra.numero}
          destino="guale"
          titulo={`Pedido Gualeguay — obra ${obra.numero}`}
          busy={busy}
          excluirIds={new Set([obra.pedidoId].filter(Boolean))}
          onSelect={handleSeleccionPedidoTela}
          onClose={() => setShowBuscarTela(false)}
        />
      )}

      {buscarComplementario && (
        <BuscarPedidoModal
          obraNumero={obra.numero}
          destino={buscarComplementario}
          titulo={`Otro pedido ${buscarComplementario === "r8" ? "R8" : "Gualeguay"} — obra ${obra.numero}`}
          busy={busy}
          excluirIds={new Set([obra.pedidoId, obra.pedidoTelaId, ...(obra.pedidosExtra || []).map(p => p.pedidoId)].filter(Boolean))}
          onSelect={handleSeleccionComplementario}
          onClose={() => setBuscarComplementario(null)}
        />
      )}

      {modalPedido && (
        <ProductosPedidoModal pedido={modalPedido} onClose={() => setModalPedido(null)} />
      )}

      {comparacion && (
        <ComparacionModal
          tipo={comparacion.tipo}
          resultado={comparacion.resultado}
          onClose={() => setComparacion(null)}
        />
      )}

      {showConfirmarOP && tieneOP && (
        <ConfirmarOPModal
          obra={obra}
          confirmando={confirmandoOP}
          onConfirmar={handleConfirmarOP}
          onReemplazar={() => setShowConfirmarOP(false)}
          onClose={() => setShowConfirmarOP(false)}
        />
      )}

      {showExport && (
        <ExportFinnegansModal
          obra={obra}
          usuario={usuario}
          itemsOverride={exportDelta}
          sufijoArchivo={exportDelta ? "_amp" : ""}
          sufijoDesc={exportDelta ? " (AMPLIACION)" : ""}
          onClose={() => { setShowExport(false); setExportDelta(null); }}
        />
      )}

      {showCambiosOP && (
        <CambiosOPModal
          obra={obra}
          onClose={() => setShowCambiosOP(false)}
          onGenerar={handleGenerarComplementario}
          onMarcarRevisado={handleMarcarRevisadoOP}
        />
      )}

      {detalleGrupo === "pedidos" && (
        <DetalleModal titulo="1 · Pedidos de venta" onClose={() => setDetalleGrupo(null)}>
          {obra.opConfirmado && (
            <div style={{ background: "#f0fdf4", borderRadius: 8, padding: "6px 10px", marginBottom: 10, fontSize: 12, color: "#15803d", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span>✓ Lectura del OP confirmada por {obra.opConfirmado.porNombre || "—"} · {formatFechaHora(obra.opConfirmado.at)}</span>
              {puedeEditar && (
                <button className="btn btn--ghost" style={{ fontSize: 11 }} onClick={() => window.confirm("¿Deshacer la confirmación de lectura del OP?") && desconfirmarLecturaOP(obra.id, usuario)}>
                  Revisar de nuevo
                </button>
              )}
            </div>
          )}
          <div style={{ display: "grid", gap: 12, fontSize: 13 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <span style={{ minWidth: 0 }}>
                {hitos.op ? "✓" : "○"} <b>Pedido OP:</b>{" "}
                {docDetalle("op", true, "adjuntar el archivo exportado de OP")}
              </span>
              <span style={{ flexShrink: 0 }}>{docAccion("op", true)}</span>
            </div>

            <div>
              <div style={{ minWidth: 0 }}>
                {hitos.vinculado ? "✓" : "○"} <b>Pedido R8</b> (perfiles, refuerzos, vidrios):{" "}
                {pedidoVinculado ? (
                  <>
                    <a
                      onClick={e => { e.preventDefault(); verProductosDelPedido(pedidoVinculado.id, pedidoVinculado); }}
                      href={`/pedidos/${encodeURIComponent(pedidoVinculado.id)}`}
                      title="Ver los productos del pedido"
                    >
                      {pedidoVinculado.id}
                    </a>
                    {pedidoVinculado.productos?.length ? ` · ${pedidoVinculado.productos.length} productos` : ""}
                  </>
                ) : obra.pedidoAdjuntoR8 ? (
                  <>
                    📎 <a href={obra.pedidoAdjuntoR8.archivo?.url} target="_blank" rel="noreferrer">{obra.pedidoAdjuntoR8.archivo?.nombre}</a>
                    <span style={{ color: obra.pedidoAdjuntoR8.productos?.length ? "#15803d" : "#c2410c" }}>
                      {obra.pedidoAdjuntoR8.productos?.length ? ` · ${obra.pedidoAdjuntoR8.productos.length} productos leídos` : " · sin productos leídos"}
                    </span>
                  </>
                ) : !tieneOP ? (
                  <span style={{ color: "#94a3b8" }}>primero el pedido OP</span>
                ) : (
                  <span style={{ color: "#c2410c" }}>generar → importar en Finnegans → vincular</span>
                )}
              </div>
              {puedeEditar && (pedidoVinculado || obra.pedidoAdjuntoR8 || tieneOP) && (
                <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                  {pedidoVinculado && (
                    <button
                      className="btn btn--ghost" style={{ fontSize: 12 }}
                      onClick={() => window.confirm(
                        hitos.despacho
                          ? "⚠️ Esta obra ya tiene despacho adjunto. ¿Desvincular el pedido igual?"
                          : "¿Desvincular el pedido de esta obra?"
                      ) && desvincularPedidoDeObra(obra.id, usuario)}
                    >
                      Desvincular
                    </button>
                  )}
                  {obra.pedidoAdjuntoR8 && permisos.admin && (
                    <button className="btn btn--ghost" style={{ fontSize: 12, color: "#b91c1c" }}
                      onClick={() => window.confirm("¿Quitar el pedido adjunto?") && eliminarPedidoManual(obra.id, "r8", usuario, obra.pedidoAdjuntoR8.archivo?.path)}>
                      Quitar pedido adjunto
                    </button>
                  )}
                  {tieneOP && !pedidoVinculado && !obra.pedidoAdjuntoR8 && (
                    <>
                      <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => setShowExport(true)}>
                        ⬇️ Generar import
                      </button>
                      <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy || !obra.opConfirmado} onClick={() => setShowBuscar(true)} title={obra.opConfirmado ? "" : "Confirmá primero la lectura del OP"}>
                        🔍 Vincular
                      </button>
                      {permisos.admin && (
                        <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={leyendoPedido} onClick={() => pedirPedidoManual("r8")}
                          title="Admin: adjuntar el PDF del pedido de Finnegans y leer sus productos (obras que no pasan por el sistema)">
                          {leyendoPedido && pedidoManualDest === "r8" ? "Leyendo…" : "📎 Adjuntar pedido (leer)"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <div style={{ minWidth: 0 }}>
                {(obra.pedidoTelaId || obra.pedidoAdjuntoGuale) ? "✓" : "○"} <b>Pedido Gualeguay</b> (accesorios Karpen + Acc. Varios + telas):{" "}
                {obra.pedidoTelaId ? (
                  <a
                    onClick={e => { e.preventDefault(); verProductosDelPedido(obra.pedidoTelaId, candidatos.find(p => p.id === obra.pedidoTelaId)); }}
                    href={`/pedidos/${encodeURIComponent(obra.pedidoTelaId)}`}
                    title="Ver los productos del pedido"
                  >
                    {obra.pedidoTelaId}
                  </a>
                ) : obra.pedidoAdjuntoGuale ? (
                  <>
                    📎 <a href={obra.pedidoAdjuntoGuale.archivo?.url} target="_blank" rel="noreferrer">{obra.pedidoAdjuntoGuale.archivo?.nombre}</a>
                    <span style={{ color: obra.pedidoAdjuntoGuale.productos?.length ? "#15803d" : "#c2410c" }}>
                      {obra.pedidoAdjuntoGuale.productos?.length ? ` · ${obra.pedidoAdjuntoGuale.productos.length} productos leídos` : " · sin productos leídos"}
                    </span>
                  </>
                ) : (
                  <span style={{ color: "#94a3b8" }}>solo si la obra lleva material de Gualeguay</span>
                )}
              </div>
              {puedeEditar && (obra.pedidoTelaId || obra.pedidoAdjuntoGuale || tieneOP) && (
                <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
                  {obra.pedidoTelaId ? (
                    <button
                      className="btn btn--ghost" style={{ fontSize: 12 }}
                      onClick={() => window.confirm("¿Desvincular el pedido de Gualeguay?") && desvincularPedidoTelaDeObra(obra.id, usuario)}
                    >
                      Desvincular
                    </button>
                  ) : obra.pedidoAdjuntoGuale ? (
                    permisos.admin && (
                      <button className="btn btn--ghost" style={{ fontSize: 12, color: "#b91c1c" }}
                        onClick={() => window.confirm("¿Quitar el pedido adjunto?") && eliminarPedidoManual(obra.id, "guale", usuario, obra.pedidoAdjuntoGuale.archivo?.path)}>
                        Quitar pedido adjunto
                      </button>
                    )
                  ) : (
                    <>
                      <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => setShowBuscarTela(true)}>
                        🔍 Vincular
                      </button>
                      {permisos.admin && (
                        <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={leyendoPedido} onClick={() => pedirPedidoManual("guale")}
                          title="Admin: adjuntar el PDF del pedido de Gualeguay y leer sus productos">
                          {leyendoPedido && pedidoManualDest === "guale" ? "Leyendo…" : "📎 Adjuntar pedido (leer)"}
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Pedidos adicionales por depósito (obras hechas por partes o ampliaciones) ── */}
            {(obra.pedidoId || (obra.pedidosExtra || []).length > 0) && (
              <div>
                <div style={{ minWidth: 0 }}>
                  {(obra.pedidosExtra || []).length > 0 ? "✓" : "○"} <b>Pedidos adicionales</b> (por partes / ampliaciones):
                </div>
                {(obra.pedidosExtra || []).map(pe => (
                  <div key={pe.pedidoId} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, fontSize: 12.5, padding: "2px 0 2px 18px" }}>
                    <span>
                      <span style={{ fontSize: 9.5, fontWeight: 700, borderRadius: 4, padding: "1px 5px", background: pe.destino === "r8" ? "#e0f2fe" : "#fef3c7", color: pe.destino === "r8" ? "#0369a1" : "#92400e" }}>
                        {pe.destino === "r8" ? "R8" : "GUALE"}
                      </span>{" "}
                      <a onClick={e => { e.preventDefault(); verProductosDelPedido(pe.pedidoId, candidatos.find(p => p.id === pe.pedidoId)); }} href={`/pedidos/${encodeURIComponent(pe.pedidoId)}`} title="Ver los productos del pedido">{pe.pedidoId}</a>
                      {pe.por ? <span style={{ color: "#94a3b8" }}> · {pe.por}</span> : null}
                    </span>
                    {puedeEditar && (
                      <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px", color: "#b91c1c", flexShrink: 0 }}
                        onClick={() => window.confirm("¿Desvincular este pedido adicional?") && desvincularPedidoComplementario(obra, pe.pedidoId, usuario)}>✕</button>
                    )}
                  </div>
                ))}
                {puedeEditar && obra.pedidoId && (
                  <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap", paddingLeft: 18 }}>
                    <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => setBuscarComplementario("r8")}>🔍 Vincular otro pedido R8</button>
                    <button className="btn btn--outline" style={{ fontSize: 12 }} disabled={busy} onClick={() => setBuscarComplementario("guale")}>🔍 Vincular otro pedido GUALE</button>
                  </div>
                )}
              </div>
            )}

            {/* ── Cruce OP ↔ pedidos de Finnegans ── */}
            <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 10, marginTop: 4 }}>
              {detallePedidos === null ? (
                <div style={{ color: "#94a3b8", fontSize: 12.5 }}>Leyendo el OP y los pedidos…</div>
              ) : detallePedidos.error ? (
                <div style={{ color: "#b91c1c", fontSize: 12.5 }}>{detallePedidos.error}</div>
              ) : (
                (() => {
                  const { items, prodR8, prodGuale, cruce } = detallePedidos;

                  // Estado por renglón del OP (según el cruce)
                  const estadoPorClave = new Map();
                  if (cruce) {
                    cruce.coinciden.forEach(f => estadoPorClave.set(f.clave, { tipo: "ok", f }));
                    cruce.cantidadDistinta.forEach(f => estadoPorClave.set(f.clave, { tipo: "dif", f }));
                    cruce.soloDocumento.forEach(it2 => estadoPorClave.set(it2.clave || it2.codNum || it2.alias || it2.detalle, { tipo: "falta" }));
                    (cruce.sustituidos || []).forEach(f => estadoPorClave.set(f.clave, { tipo: "sust", f }));
                  }
                  const sobrantes = new Set((cruce?.soloPedido || []).map(p => p.cod));
                  const difs = cruce
                    ? cruce.cantidadDistinta.length + cruce.soloPedido.length + cruce.soloDocumento.length +
                      (cruce.grupos || []).filter(g => !g.enPedido || !g.cantidadOk).length
                    : null;

                  const badgeRenglon = (it) => {
                    if (!cruce) return null;
                    // Ítems que esperan el pedido GUALE: pendientes, no diferencias
                    if (cruce.faltaGuale && (it.categoria === "karpen" || it.categoria === "plaza")) {
                      return <span style={{ color: "#94a3b8", fontWeight: 600, whiteSpace: "nowrap" }}>🕐 pedido GUALE</span>;
                    }
                    const st = estadoPorClave.get(it.codNum || it.alias || it.detalle);
                    if (st?.tipo === "ok") return <span style={{ color: "#15803d", fontWeight: 700, whiteSpace: "nowrap" }}>✓</span>;
                    if (st?.tipo === "dif") return <span style={{ color: "#b45309", fontWeight: 700, whiteSpace: "nowrap" }}>⚠ {st.f.cantDoc}≠{st.f.cantPedido}</span>;
                    if (st?.tipo === "falta") return <span style={{ color: "#b91c1c", fontWeight: 700, whiteSpace: "nowrap" }}>✗ falta</span>;
                    if (st?.tipo === "sust") return <span style={{ color: "#0369a1", fontWeight: 700, whiteSpace: "nowrap" }} title={`Reemplazado por ${st.f.cod} (${st.f.porNombre || "—"})`}>↔ {st.f.cantPedido}× {st.f.cod}</span>;
                    return null;
                  };

                  return (
                    <>
                      {cruce ? (
                        <div style={{
                          fontSize: 12.5, fontWeight: 700, padding: "8px 10px", borderRadius: 8, marginBottom: 8,
                          background: difs > 0 ? "#fff1f2" : cruce.faltaGuale ? "#fffbeb" : "#f0fdf4",
                          color: difs > 0 ? "#b91c1c" : cruce.faltaGuale ? "#92400e" : "#15803d",
                        }}>
                          {difs > 0
                            ? `⚠ ${difs} diferencia${difs === 1 ? "" : "s"} entre el OP y los pedidos — revisar abajo`
                            : cruce.faltaGuale
                              ? "✔ Pedido R8 verificado — falta vincular el pedido GUALE para cubrir accesorios y telas"
                              : "✅ Todos los productos del OP están cubiertos por los pedidos de Finnegans"}
                        </div>
                      ) : (
                        <div style={{ fontSize: 12, color: "#b45309", marginBottom: 8 }}>
                          Vinculá el pedido R8 para ver el control cruzado.
                        </div>
                      )}

                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, margin: "4px 0 6px" }}>
                        PEDIDO DE OP
                      </div>

                      {(() => {
                        const grupos = { perfiles: [], refuerzos: [], karpen: [], plaza: [], vidrios: [], telas: [], terceros: [] };
                        for (const it of items) {
                          if (it.categoria === "perfiles" || it.categoria === "finnegans") {
                            (/^R\d/i.test(it.alias) ? grupos.refuerzos : grupos.perfiles).push(it);
                          } else if (grupos[it.categoria]) {
                            grupos[it.categoria].push(it);
                          }
                        }
                        // Estado de los grupos agregados (Acc. Varios / Vidrios / telas)
                        const grupoCruce = (key) => cruce?.grupos?.find(g => g.key === key) || null;
                        const chipPendienteGuale = <span style={{ color: "#94a3b8", fontWeight: 600 }}>🕐 pedido GUALE</span>;
                        const chipGrupo = (g) => {
                          if (!cruce) return null;
                          if (!g) return null;
                          return g.enPedido && g.cantidadOk
                            ? <span style={{ color: "#15803d", fontWeight: 700 }}>✓ {g.prodNombre} · {g.cantPedido}</span>
                            : !g.enPedido
                              ? <span style={{ color: "#b91c1c", fontWeight: 700 }}>✗ falta "{g.prodNombre}"</span>
                              : <span style={{ color: "#b45309", fontWeight: 700 }}>⚠ {g.cantDoc}≠{g.cantPedido}</span>;
                        };
                        const SECCIONES = [
                          { key: "perfiles", label: "Perfiles", porRenglon: true },
                          { key: "refuerzos", label: "Refuerzos", porRenglon: true },
                          { key: "karpen", label: "Accesorios Karpen", porRenglon: true },
                          { key: "plaza", label: "Accesorios de plaza", chip: () => cruce?.faltaGuale ? chipPendienteGuale : chipGrupo(grupoCruce("plaza")) },
                          { key: "vidrios", label: "Vidrios", chip: () => chipGrupo(grupoCruce("vidrios")) },
                          {
                            key: "telas", label: "Tela mosquitera",
                            chip: () => cruce?.faltaGuale ? chipPendienteGuale
                              : cruce?.telas
                              ? (cruce.telas.enPedido && cruce.telas.cantidadOk
                                  ? <span style={{ color: "#15803d", fontWeight: 700 }}>✓ {cruce.telas.prodPedido.cant} rollos</span>
                                  : cruce.telas.enPedido
                                    ? <span style={{ color: "#b45309", fontWeight: 700 }}>⚠ {cruce.telas.prodPedido.cant} vs {cruce.telas.calculo.rollosTotal.toFixed(2)}</span>
                                    : <span style={{ color: "#b91c1c", fontWeight: 700 }}>✗ sin tejido en el pedido</span>)
                              : null,
                          },
                          { key: "terceros", label: "Anodizado / terceros", nota: "no pasa por Finnegans" },
                        ];
                        return SECCIONES.filter(s => grupos[s.key].length > 0).map(s => {
                          const itemsSec = grupos[s.key];
                          const totalCant = itemsSec.reduce((sum, it) => sum + it.cant, 0);
                          return (
                            <div key={s.key} style={{ marginBottom: 8 }}>
                              <div style={{
                                display: "flex", justifyContent: "space-between", gap: 8, fontSize: 11.5,
                                fontWeight: 700, color: "#475569", background: "#f8fafc",
                                borderRadius: 6, padding: "4px 8px", marginBottom: 2, flexWrap: "wrap",
                              }}>
                                <span>{s.label} <span style={{ color: "#94a3b8", fontWeight: 600 }}>· {itemsSec.length} ítems · {Math.round(totalCant * 100) / 100} unid.</span></span>
                                <span>{s.chip ? s.chip() : s.nota ? <span style={{ color: "#94a3b8", fontWeight: 600 }}>{s.nota}</span> : null}</span>
                              </div>
                              {itemsSec.map((it, i) => {
                                const clave = it.codNum || it.alias || it.detalle;
                                const st = cruce ? estadoPorClave.get(clave) : null;
                                return (
                                  <div key={i}>
                                    <div style={{
                                      display: "grid", gridTemplateColumns: "38px 82px 1fr auto", gap: 8, fontSize: 12, color: "#475569",
                                      padding: "3px 8px", borderBottom: i < itemsSec.length - 1 ? "1px solid #f8fafc" : "none",
                                      alignItems: "baseline",
                                    }}>
                                      <span style={{ fontWeight: 700, textAlign: "right", color: "#0f172a" }}>{it.cant}×</span>
                                      <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{it.alias}</span>
                                      <span style={{ color: "#94a3b8", overflowWrap: "anywhere" }}>
                                        {it.detalle}
                                      </span>
                                      <span style={{ display: "flex", gap: 4, alignItems: "center", whiteSpace: "nowrap" }}>
                                      {s.porRenglon && badgeRenglon(it)}
                                      {s.porRenglon && st?.tipo === "falta" && puedeEditar && cruce.soloPedido.length > 0 && (
                                        <button
                                          className="btn btn--ghost" style={{ fontSize: 11, padding: "1px 7px" }}
                                          onClick={() => setSustituyendo(sustituyendo === clave ? null : clave)}
                                        >
                                          Reemplazo…
                                        </button>
                                      )}
                                      {s.porRenglon && st?.tipo === "sust" && puedeEditar && (
                                        <button
                                          className="btn btn--ghost" style={{ fontSize: 11, padding: "1px 7px" }}
                                          onClick={() => quitarSustitucion(clave)}
                                        >
                                          Quitar
                                        </button>
                                      )}
                                      </span>
                                    </div>
                                    {s.porRenglon && sustituyendo === clave && st?.tipo === "falta" && (
                                      <div style={{ padding: "3px 8px 6px 42px" }}>
                                        <select
                                          defaultValue=""
                                          onChange={e => {
                                            const p = cruce.soloPedido.find(x => String(x.cod) === e.target.value);
                                            if (p) { marcarSustitucion(clave, p); setSustituyendo(null); }
                                          }}
                                          style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #f59e0b", maxWidth: "100%" }}
                                        >
                                          <option value="" disabled>¿Qué producto de Finnegans lo reemplaza?</option>
                                          {cruce.soloPedido.map(p => (
                                            <option key={p.cod} value={p.cod}>{p.cant}× {(p.desc || p.cod).slice(0, 55)}</option>
                                          ))}
                                        </select>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          );
                        });
                      })()}

                      {/* ── Pedidos de Finnegans unificados ── */}
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, margin: "12px 0 6px" }}>
                        PEDIDOS FINNEGANS · UNIFICADOS ({(prodR8?.length || 0) + (prodGuale?.length || 0)})
                      </div>
                      {(() => {
                        const unificados = [
                          ...(prodR8 || []).map(p => ({ ...p, origen: "R8" })),
                          ...(prodGuale || []).map(p => ({ ...p, origen: "GUALE" })),
                        ];
                        if (unificados.length === 0) {
                          return <div style={{ color: "#94a3b8", fontSize: 12.5 }}>Sin pedidos de Finnegans vinculados.</div>;
                        }
                        const codsSustitutos = new Set((cruce?.sustituidos || []).map(f => String(f.cod)));
                        return unificados.map((p, i) => {
                          const cod = String(p.cod || "").trim();
                          const sobra = cruce && sobrantes.has(cod);
                          const esSust = codsSustitutos.has(cod);
                          return (
                            <div key={i} style={{
                              display: "grid", gridTemplateColumns: "46px 40px 1fr auto", gap: 8, fontSize: 12, padding: "3px 8px",
                              borderBottom: "1px solid #f8fafc", alignItems: "baseline",
                              color: sobra ? "#b91c1c" : "#475569",
                              background: sobra ? "#fff1f2" : "transparent",
                            }}>
                              <span style={{
                                fontSize: 9.5, fontWeight: 700, borderRadius: 4, padding: "1px 5px", textAlign: "center",
                                background: p.origen === "R8" ? "#e0f2fe" : "#fef3c7",
                                color: p.origen === "R8" ? "#0369a1" : "#92400e", alignSelf: "center",
                              }}>
                                {p.origen}
                              </span>
                              <span style={{ fontWeight: 700, textAlign: "right" }}>{p.cant ?? p.cantidad}×</span>
                              <span style={{ overflowWrap: "anywhere" }}>
                                {p.desc || p.descripcion || p.cod}
                              </span>
                              <span style={{ whiteSpace: "nowrap", fontWeight: 700 }}>
                                {sobra ? "✗ no está en el OP" : esSust ? <span style={{ color: "#0369a1" }}>↔ reemplazo</span> : ""}
                              </span>
                            </div>
                          );
                        });
                      })()}
                    </>
                  );
                })()
              )}
            </div>
          </div>
        </DetalleModal>
      )}

      {detalleGrupo === "despachos" && (
        <DetalleModal titulo="4 · Productos despachados" onClose={() => setDetalleGrupo(null)}>
          <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 10 }}>
            Productos leídos automáticamente de cada remito adjunto.
          </div>
          {detalleDespachos === null ? (
            <div style={{ color: "#94a3b8", fontSize: 12.5 }}>Leyendo los remitos…</div>
          ) : detalleDespachos.error ? (
            <div style={{ color: "#b91c1c", fontSize: 12.5 }}>{detalleDespachos.error}</div>
          ) : detalleDespachos.grupos.length === 0 ? (
            <div style={{ color: "#94a3b8", fontSize: 12.5 }}>Todavía no hay remitos adjuntos.</div>
          ) : (
            detalleDespachos.grupos.map((g, gi) => {
              const totalCant = g.items.reduce((s, it) => s + (Number(it.cant) || 0), 0);
              return (
                <div key={gi} style={{ marginBottom: 12 }}>
                  <div style={{
                    display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap",
                    fontSize: 11.5, fontWeight: 700, color: "#475569", background: "#f8fafc",
                    borderRadius: 6, padding: "5px 9px", marginBottom: 3,
                  }}>
                    <span>
                      <span style={{ fontSize: 9.5, borderRadius: 4, padding: "1px 5px", marginRight: 5, background: g.destino === "r8" ? "#e0f2fe" : "#fef3c7", color: g.destino === "r8" ? "#0369a1" : "#92400e" }}>
                        {g.destino === "r8" ? "R8" : "GUALE"}
                      </span>
                      {g.label}
                    </span>
                    <span style={{ color: "#94a3b8", fontWeight: 600 }}>
                      {g.items.length} ítems · {Math.round(totalCant * 100) / 100} unid.
                    </span>
                  </div>
                  {g.archivo && (
                    <div style={{ fontSize: 11.5, paddingLeft: 9, marginBottom: 3 }}>
                      📎 <a href={g.archivo.url} target="_blank" rel="noreferrer">{g.archivo.nombre}</a>
                    </div>
                  )}
                  {g.error ? (
                    <div style={{ fontSize: 12, color: "#c2410c", paddingLeft: 9 }}>⚠ No se pudieron leer los productos: {g.error}</div>
                  ) : g.items.length === 0 ? (
                    <div style={{ fontSize: 12, color: "#94a3b8", paddingLeft: 9 }}>Sin productos leídos.</div>
                  ) : (
                    g.items.map((it, i) => (
                      <div key={i} style={{
                        display: "grid", gridTemplateColumns: "48px 90px 1fr", gap: 8, fontSize: 12, color: "#475569",
                        padding: "3px 9px", borderBottom: i < g.items.length - 1 ? "1px solid #f8fafc" : "none",
                        alignItems: "baseline",
                      }}>
                        <span style={{ fontWeight: 700, textAlign: "right", color: "#0f172a" }}>{it.cant}×</span>
                        <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{it.codNum}</span>
                        <span style={{ color: "#94a3b8", overflowWrap: "anywhere" }}>{it.detalle}</span>
                      </div>
                    ))
                  )}
                </div>
              );
            })
          )}
        </DetalleModal>
      )}

      {/* ── Historial ── */}
      <div style={cardStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0, fontSize: 15 }}>Historial</h3>
          <button className="btn btn--ghost" style={{ fontSize: 12.5 }} onClick={() => setVerHistorial(v => !v)}>
            {verHistorial ? "Ocultar" : `Ver (${obra.historial?.length || 0})`}
          </button>
        </div>
        {verHistorial && (
          <div style={{ marginTop: 8 }}>
            {/* El balance es información reservada: sus eventos solo los ve finanzas */}
            {[...(obra.historial || [])]
              .filter(h => puedeFinanzas || !String(h.evento || "").startsWith("BALANCE"))
              .reverse().map((h, i) => (
              <div key={i} style={{ fontSize: 12.5, padding: "4px 0", borderBottom: "1px solid #f8fafc", color: "#475569" }}>
                <b>{EVENTO_LABELS[h.evento] || h.evento || h.estado}</b>
                {h.nota ? ` — ${h.nota}` : ""}
                <span style={{ color: "#94a3b8" }}>
                  {" "}· {h.auto ? "automático" : h.porNombre || "—"} · {formatFechaHora(h.at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Notas ── */}
      <div style={cardStyle}>
        <h3 style={{ margin: "0 0 8px", fontSize: 15 }}>Notas</h3>
        <textarea
          value={notasDraft ?? obra.notas ?? ""}
          disabled={!puedeEditar}
          onChange={e => setNotasDraft(e.target.value)}
          placeholder="Notas internas de la obra…"
          style={{ width: "100%", boxSizing: "border-box", minHeight: 70, padding: "8px 10px", fontSize: 13.5, border: "1px solid #cbd5e1", borderRadius: 8, resize: "vertical" }}
        />
        {notasDraft != null && notasDraft !== (obra.notas || "") && (
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button className="btn" onClick={handleGuardarNotas}>Guardar notas</button>
            <button className="btn btn--ghost" onClick={() => setNotasDraft(null)}>Cancelar</button>
          </div>
        )}
      </div>

      </div>{/* fin columna izquierda */}

      {/* ── Panel lateral de resumen ── */}
      <div style={{ display: "grid", gap: 8, alignContent: "start" }}>
        {(() => {
          const resp = responsableActual(obra);
          const HITO_CORTO = {
            presupuesto: "adjuntar el presupuesto",
            pedido: "crear y vincular el pedido",
            facturaCompra: "compra de accesorios",
            recepcionCompra: "compra de accesorios",
            despacho: "despacho R8",
            despachoGuale: "despacho Gualeguay",
            entregada: "marcar la entrega",
            cobrada: "cobrar la obra",
            facturada: "facturar",
            balance: "hacer el balance",
          };
          return (
            <div style={cardStyle}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>RESPONSABLE ACTUAL</div>
              {resp ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{
                    width: 28, height: 28, borderRadius: "50%", background: resp.bg, color: resp.color,
                    fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>
                    {(resp.nombre || "—")[0]}
                  </span>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700 }}>{resp.nombre || "—"}</div>
                    <div style={{ fontSize: 11.5, color: "#64748b" }}>
                      {resp.hito === "balance" && !puedeFinanzas ? "cierre administrativo" : HITO_CORTO[resp.hito]}
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "#15803d", fontWeight: 700 }}>✅ Obra finalizada</div>
              )}
            </div>
          );
        })()}

        <div style={cardStyle}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>DATOS</div>
          <div style={{ fontSize: 12.5, display: "grid", gap: 6 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, color: "#475569" }}>
              🔨
              <select
                value={obra.carpintero || ""}
                disabled={!puedeEditar}
                onChange={e => actualizarObra(obra.id, { carpintero: e.target.value || null })}
                style={{ flex: 1, padding: "4px 8px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12.5 }}
              >
                <option value="">Sin carpintero</option>
                {CARPINTEROS.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            {/* Cliente — editable por si se eligió mal al crear la obra */}
            {editandoCliente ? (
              <div style={{ display: "grid", gap: 4 }}>
                <input
                  autoFocus
                  value={qClienteEdit}
                  onChange={e => setQClienteEdit(e.target.value)}
                  placeholder="Buscar cliente por nombre o código…"
                  style={{ width: "100%", boxSizing: "border-box", padding: "5px 8px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 12.5 }}
                />
                {clientesLista.length === 0 && <div style={{ color: "#94a3b8", fontSize: 11.5 }}>Cargando clientes…</div>}
                {clientesFiltradosEdit.map(c => (
                  <div
                    key={c.codigo}
                    onClick={() => handleCambiarCliente(c)}
                    style={{ fontSize: 12, padding: "5px 7px", cursor: "pointer", borderRadius: 6, background: "#f8fafc" }}
                  >
                    {c.nombre} <span style={{ color: "#94a3b8" }}>· {c.codigo}</span>
                  </div>
                ))}
                {qClienteEdit.trim().length >= 2 && clientesFiltradosEdit.length === 0 && clientesLista.length > 0 && (
                  <div style={{ color: "#94a3b8", fontSize: 11.5 }}>Sin resultados</div>
                )}
                <button className="btn btn--ghost" style={{ fontSize: 11.5, justifySelf: "start" }}
                  onClick={() => { setEditandoCliente(false); setQClienteEdit(""); }}>
                  Cancelar
                </button>
              </div>
            ) : (
              <div style={{ color: "#475569", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                <span style={{ minWidth: 0 }}>
                  👤 {obra.cliente || "—"}{obra.clienteCodigo ? <span style={{ color: "#94a3b8" }}> · {obra.clienteCodigo}</span> : null}
                </span>
                {puedeEditar && (
                  <button className="btn btn--ghost" title="Corregir el cliente"
                    style={{ fontSize: 11.5, padding: "0 6px", flexShrink: 0 }}
                    onClick={() => setEditandoCliente(true)}>
                    ✏️
                  </button>
                )}
              </div>
            )}
            {obra.nombreObra && <div style={{ color: "#475569" }}>📍 {obra.nombreObra}</div>}
          </div>
        </div>

        {/* ── Cobranza (paralela al proceso — todo el staff de obras) ── */}
        {(() => {
          const totalCobrado = cobranzas.reduce((s, c) => s + (Number(c.monto) || 0), 0);
          const monto = Number(obra.montoPresupuesto) || 0;
          const falta = monto ? Math.max(0, monto - totalCobrado) : null;
          const money = (v) => `${obra.monedaPresupuesto || "$"} ${Number(v).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`;
          // Solo el presupuesto muestra su equivalente con IVA (+22%) en gris.
          const iva = (v) => <span style={{ color: "#94a3b8", fontWeight: 400, fontSize: 11 }}> · {money(v * 1.22)} c/IVA</span>;
          return (
            <div style={cardStyle}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5 }}>COBRANZA</span>
                {puedeEditar && (
                  <button className="btn btn--ghost" title="Registrar un pago"
                    style={{ fontSize: 15, lineHeight: 1, padding: "0 8px" }}
                    onClick={() => setMostrarFormCobranza(v => !v)}>
                    {mostrarFormCobranza ? "×" : "+"}
                  </button>
                )}
              </div>

              <div style={{ display: "grid", gap: 3, fontSize: 12.5, marginBottom: 8 }}>
                {monto > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                    <span style={{ color: "#64748b" }}>Presupuesto</span>
                    <span style={{ textAlign: "right" }}><b>{money(monto)}</b>{iva(monto)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "#64748b" }}>Cobrado</span><b style={{ color: "#15803d" }}>{money(totalCobrado)}</b>
                </div>
                {falta != null && (
                  <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 3 }}>
                    <span style={{ color: "#64748b" }}>Falta</span>
                    <b style={{ color: falta > 0 ? "#c2410c" : "#15803d" }}>{money(falta)}</b>
                  </div>
                )}
              </div>

              {cobranzas.length === 0 ? (
                <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 8 }}>Sin pagos cargados todavía.</div>
              ) : (
                <div style={{ marginBottom: 8, display: "grid", gap: 6 }}>
                  {cobranzas.map(c => {
                    const docSlot = (campo, label, quien, puede) => {
                      const doc = c[campo];
                      return (
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, fontSize: 11.5 }}>
                          <span style={{ color: doc ? "#15803d" : "#b45309", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {doc ? "✓" : "○"} {label}
                          </span>
                          {doc
                            ? <a href={doc.url} target="_blank" rel="noreferrer" style={{ flexShrink: 0 }}>ver</a>
                            : puede
                              ? <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px", flexShrink: 0 }} onClick={() => pedirDocCobranza(c.id, campo)}>Adjuntar</button>
                              : <span style={{ color: "#cbd5e1", flexShrink: 0 }}>{quien}</span>}
                        </div>
                      );
                    };
                    // En efectivo no hay comprobante de pago para adjuntar
                    const completo = (c.efectivo || c.comprobante) && c.finnegans;
                    const enEdicion = editandoCobranza === c.id;
                    return (
                      <div key={c.id} style={{ border: `1px solid ${completo ? "#e2e8f0" : "#fde68a"}`, borderRadius: 8, padding: "7px 9px" }}>
                        {enEdicion ? (
                          /* Edición del pago */
                          <form onSubmit={handleEditarCobranza} style={{ display: "grid", gap: 5 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>EDITAR PAGO</div>
                            <div style={{ display: "flex", gap: 4 }}>
                              <input type="number" step="0.01" min="0" value={editCobranza.monto} autoFocus
                                onChange={e => setEditCobranza(f => ({ ...f, monto: e.target.value }))}
                                placeholder="Monto" style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                              <input type="date" value={editCobranza.fecha}
                                onChange={e => setEditCobranza(f => ({ ...f, fecha: e.target.value }))}
                                style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                            </div>
                            <input value={editCobranza.nota}
                              onChange={e => setEditCobranza(f => ({ ...f, nota: e.target.value }))}
                              placeholder="Nota (seña, saldo, adelanto…)"
                              style={{ fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
                              <input type="checkbox" checked={editCobranza.efectivo}
                                onChange={e => setEditCobranza(f => ({ ...f, efectivo: e.target.checked }))} />
                              Pago en efectivo (sin comprobante)
                            </label>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button type="submit" className="btn btn--principal" style={{ fontSize: 12, flex: 1 }}>Guardar</button>
                              <button type="button" className="btn btn--ghost" style={{ fontSize: 12 }}
                                onClick={() => setEditandoCobranza(null)}>Cancelar</button>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, marginBottom: 5 }}>
                              <span style={{ fontSize: 12.5, minWidth: 0 }}>
                                <b>{money(c.monto)}</b>
                                {c.efectivo && <span style={{ fontSize: 10, fontWeight: 700, background: "#dcfce7", color: "#15803d", borderRadius: 999, padding: "1px 6px", marginLeft: 5 }}>efectivo</span>}
                                {c.nota ? <div style={{ color: "#475569", fontSize: 11 }}>{c.nota}</div> : null}
                                <div style={{ color: "#94a3b8", fontSize: 11 }}>{formatFechaHora(c.fecha) || "—"}{c.porNombre ? ` · ${c.porNombre}` : ""}</div>
                              </span>
                              {puedeEditar && (
                                <span style={{ display: "inline-flex", gap: 2, flexShrink: 0 }}>
                                  <button className="btn btn--ghost" title="Editar pago" style={{ fontSize: 11, padding: "0 6px" }}
                                    onClick={() => abrirEdicionCobranza(c)}>✎</button>
                                  <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px", color: "#b91c1c" }}
                                    onClick={() => window.confirm("¿Eliminar este pago?") && eliminarCobranza(obra.id, c.id, c.monto)}>✕</button>
                                </span>
                              )}
                            </div>
                            <div style={{ display: "grid", gap: 3 }}>
                              {c.efectivo
                                ? <div style={{ fontSize: 11.5, color: "#15803d" }}>✓ Efectivo · sin comprobante</div>
                                : docSlot("comprobante", "Comprobante de pago", "Ceci", puedeEditar)}
                              {docSlot("finnegans", "Cobranza Finnegans", "Marcela", permisos.despacho || permisos.admin)}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {puedeEditar && mostrarFormCobranza && (
                <form onSubmit={handleAgregarCobranza} style={{ display: "grid", gap: 5, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 10px", marginTop: 2 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>NUEVO PAGO</div>
                  <div style={{ display: "flex", gap: 4 }}>
                    <input type="number" step="0.01" min="0" value={nuevaCobranza.monto} autoFocus
                      onChange={e => setNuevaCobranza(c => ({ ...c, monto: e.target.value }))}
                      placeholder="Monto" style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                    <input type="date" value={nuevaCobranza.fecha}
                      onChange={e => setNuevaCobranza(c => ({ ...c, fecha: e.target.value }))}
                      style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                  </div>
                  <input value={nuevaCobranza.nota}
                    onChange={e => setNuevaCobranza(c => ({ ...c, nota: e.target.value }))}
                    placeholder="Nota (seña, saldo, adelanto…)"
                    style={{ fontSize: 12, padding: "6px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                  <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#475569", cursor: "pointer" }}>
                    <input type="checkbox" checked={nuevaCobranza.efectivo}
                      onChange={e => setNuevaCobranza(c => ({ ...c, efectivo: e.target.checked }))} />
                    Pago en efectivo (sin comprobante)
                  </label>
                  <div style={{ display: "flex", gap: 4 }}>
                    <button type="submit" className="btn btn--principal" style={{ fontSize: 12, flex: 1 }}>Registrar pago</button>
                    <button type="button" className="btn btn--ghost" style={{ fontSize: 12 }}
                      onClick={() => setMostrarFormCobranza(false)}>Cancelar</button>
                  </div>
                </form>
              )}
              <input ref={cobranzaFileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx" style={{ display: "none" }} onChange={handleDocCobranza} />
            </div>
          );
        })()}

        {/* ── Facturación — derivada de la cobranza (no efectivo→Finnegans/Marcela · efectivo→Aéreo/Judy) ── */}
        {(() => {
          const money = (v) => `${obra.monedaPresupuesto || "$"} ${Number(v).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`;
          const anticipada = facturacionHabilitada(obra);
          const habilitado = hitoHabilitado(obra, "facturada");
          const puedeMarcela = permisos.despacho || permisos.admin;
          const presupuesto = Number(obra.montoPresupuesto) || 0;
          const totalCobradoLive = cobranzas.reduce((s, c) => s + (Number(c.monto) || 0), 0);
          const faltaCobrar = Math.max(0, presupuesto - totalCobradoLive);
          // Objetivos calculados EN VIVO desde las cobranzas (no del rollup, que
          // puede quedar desactualizado): no efectivo → Finnegans · efectivo → Aéreo
          const r2 = (v) => Math.round(v * 100) / 100;
          const objBlanco = r2(cobranzas.filter(c => !c.efectivo).reduce((s, c) => s + (Number(c.monto) || 0), 0)); // Finnegans (Marcela)
          const objNegro = r2(cobranzas.filter(c => c.efectivo).reduce((s, c) => s + (Number(c.monto) || 0), 0));    // Aéreo (Judy)
          const aNombre = obra.facturaANombreDe?.nombre
            ? obra.facturaANombreDe
            : (obra.cliente ? { nombre: obra.cliente, codigo: obra.clienteCodigo || null } : null);

          const clientesFactura = (() => {
            const t = qNombreFactura.trim().toLowerCase();
            if (t.length < 2) return [];
            return clientesLista.filter(c => c.nombre.toLowerCase().includes(t) || String(c.codigo).includes(t)).slice(0, 6);
          })();

          // Una vía de facturación (Finnegans o Aéreo)
          const seccionFacturas = (tipo, label, quien, objetivo, puedeCargar, fileRef) => {
            const lista = facturas.filter(f => (f.tipo || "blanco") === tipo);
            if (objetivo <= 0.01 && lista.length === 0) return null;
            const total = lista.reduce((s, f) => s + (Number(f.monto) || 0), 0);
            const cubierto = objetivo <= 0.01 || total >= objetivo - 0.01;
            const form = facturaForm[tipo];
            const file = facturaFileByTipo[tipo];
            return (
              <div style={{ marginTop: 10, borderTop: "1px solid #e2e8f0", paddingTop: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>
                  <b style={{ fontSize: 12.5, color: cubierto ? "#15803d" : "#c2410c" }}>
                    {money(total)} <span style={{ fontWeight: 400, color: "#94a3b8" }}>/ {money(objetivo)}</span>
                  </b>
                </div>
                <div style={{ fontSize: 11, color: cubierto ? "#15803d" : "#c2410c", marginBottom: 4 }}>
                  {cubierto ? "✓ facturado completo" : `falta facturar ${money(objetivo - total)}`}
                </div>
                {lista.map(f => (
                  <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6, fontSize: 11.5, padding: "4px 0", borderBottom: "1px solid #f8fafc" }}>
                    <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                      <b>{money(f.monto)}</b>
                      {f.nota ? <span style={{ color: "#94a3b8" }}> · N° {f.nota}</span> : null}
                      {f.archivo
                        ? <> · <a href={f.archivo.url} target="_blank" rel="noreferrer">ver archivo</a></>
                        : <span style={{ color: "#c2410c" }}> · sin archivo</span>}
                    </span>
                    {puedeCargar && (
                      <button className="btn btn--ghost" style={{ fontSize: 11, padding: "0 5px", color: "#b91c1c", flexShrink: 0 }}
                        onClick={() => window.confirm("¿Eliminar esta factura?") && eliminarFactura(obra.id, f.id, f.monto, tipo)}>✕</button>
                    )}
                  </div>
                ))}
                {puedeCargar && !cubierto && (
                  <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 9px", marginTop: 6 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, color: "#64748b", marginBottom: 5 }}>+ NUEVA FACTURA</div>
                    <form onSubmit={e => handleAgregarFacturaTipo(tipo, e)} style={{ display: "grid", gap: 5 }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <input type="number" step="0.01" min="0" value={form.monto}
                          onChange={e => setFacturaForm(s => ({ ...s, [tipo]: { ...s[tipo], monto: e.target.value } }))}
                          placeholder="Monto" style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                        <input value={form.nota}
                          onChange={e => setFacturaForm(s => ({ ...s, [tipo]: { ...s[tipo], nota: e.target.value } }))}
                          placeholder="N° factura" style={{ flex: 1, minWidth: 0, fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                      </div>
                      <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.xls,.xlsx" style={{ display: "none" }}
                        onChange={e => { const fl = e.target.files?.[0] || null; setFacturaFileByTipo(s => ({ ...s, [tipo]: fl })); }} />
                      <button type="button" className={file ? "btn btn--ghost" : "btn btn--outline"} style={{ fontSize: 12, width: "100%" }}
                        onClick={() => fileRef.current?.click()}>
                        {file ? `✓ ${file.name}` : "📎 Adjuntar archivo de la factura"}
                      </button>
                      <button type="submit" className="btn btn--principal" style={{ fontSize: 12, width: "100%" }} disabled={!file || !(Number(form.monto) > 0)}>
                        Agregar factura
                      </button>
                    </form>
                  </div>
                )}
                {!puedeCargar && !cubierto && (
                  <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>La carga la hace {quien}.</div>
                )}
              </div>
            );
          };

          return (
            <div style={cardStyle}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>FACTURACIÓN</div>

              {!habilitado ? (
                <div>
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Se factura cuando la obra esté cobrada.
                    {faltaCobrar > 0 && <> Falta cobrar <b style={{ color: "#c2410c" }}>{money(faltaCobrar)}</b>.</>}
                  </div>
                  {puedeFinanzas && (
                    pidiendoAnticipada ? (
                      <form onSubmit={handleHabilitarAnticipada} style={{ display: "grid", gap: 4, marginTop: 8 }}>
                        <input autoFocus value={motivoAnticipada} onChange={e => setMotivoAnticipada(e.target.value)}
                          placeholder="Motivo (ej: el cliente pidió factura del 50%)"
                          style={{ fontSize: 11.5, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                        <div style={{ display: "flex", gap: 4 }}>
                          <button type="submit" className="btn btn--principal" style={{ fontSize: 11.5, flex: 1 }}>Habilitar</button>
                          <button type="button" className="btn btn--ghost" style={{ fontSize: 11.5 }}
                            onClick={() => { setPidiendoAnticipada(false); setMotivoAnticipada(""); }}>Cancelar</button>
                        </div>
                      </form>
                    ) : (
                      <button className="btn btn--ghost" style={{ fontSize: 11.5, marginTop: 6, width: "100%" }}
                        onClick={() => setPidiendoAnticipada(true)}>
                        Habilitar facturación anticipada
                      </button>
                    )
                  )}
                </div>
              ) : (
                <div>
                  {anticipada && (
                    <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 6, padding: "5px 8px", fontSize: 11, color: "#92400e", marginBottom: 8 }}>
                      <b>Facturación anticipada</b> · {obra.facturacionAnticipada?.porNombre || "—"}
                      {obra.facturacionAnticipada?.motivo ? <div style={{ fontStyle: "italic" }}>“{obra.facturacionAnticipada.motivo}”</div> : null}
                      {puedeFinanzas && (
                        <button className="btn btn--ghost" style={{ fontSize: 10.5, padding: "0 5px", marginTop: 2 }}
                          onClick={() => window.confirm("¿Quitar la habilitación anticipada?") && quitarFacturacionAnticipada(obra.id, usuario)}>
                          quitar
                        </button>
                      )}
                    </div>
                  )}

                  {/* A nombre de quién va la factura */}
                  {editandoNombreFactura ? (
                    <div style={{ display: "grid", gap: 4, marginBottom: 8 }}>
                      <div style={{ fontSize: 11.5, color: "#64748b" }}>A nombre de</div>
                      {nombreFactura ? (
                        <div style={{ fontSize: 12, background: "#f0fdf4", borderRadius: 6, padding: "4px 7px", display: "flex", justifyContent: "space-between", gap: 6 }}>
                          <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                            {nombreFactura.nombre}{nombreFactura.codigo ? <span style={{ color: "#94a3b8" }}> · {nombreFactura.codigo}</span> : null}
                          </span>
                          <button type="button" className="btn btn--ghost" style={{ fontSize: 10.5, padding: "0 5px", flexShrink: 0 }}
                            onClick={() => setNombreFactura(null)}>cambiar</button>
                        </div>
                      ) : (
                        <>
                          <input value={qNombreFactura} onChange={e => setQNombreFactura(e.target.value)} autoFocus
                            placeholder="Buscar cliente…" style={{ width: "100%", boxSizing: "border-box", fontSize: 11.5, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }} />
                          {clientesLista.length === 0 && <div style={{ fontSize: 11, color: "#94a3b8" }}>Cargando clientes…</div>}
                          {clientesFactura.map(c => (
                            <div key={c.codigo} onClick={() => { setNombreFactura(c); setQNombreFactura(""); }}
                              style={{ fontSize: 11.5, padding: "4px 6px", cursor: "pointer", borderRadius: 5, background: "#f8fafc", marginTop: 2 }}>
                              {c.nombre} <span style={{ color: "#94a3b8" }}>· {c.codigo}</span>
                            </div>
                          ))}
                        </>
                      )}
                      <div style={{ display: "flex", gap: 4 }}>
                        <button className="btn btn--principal" style={{ fontSize: 11.5, flex: 1 }} disabled={!nombreFactura}
                          onClick={handleGuardarNombreFactura}>Guardar</button>
                        <button className="btn btn--ghost" style={{ fontSize: 11.5 }}
                          onClick={() => { setEditandoNombreFactura(false); setQNombreFactura(""); }}>Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 6, marginBottom: 4 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11.5, color: "#64748b" }}>A nombre de</div>
                        <div style={{ fontSize: 12.5, fontWeight: 600, overflowWrap: "anywhere" }}>
                          {aNombre?.nombre || "—"}
                          {aNombre?.codigo ? <span style={{ color: "#94a3b8", fontWeight: 400 }}> · {aNombre.codigo}</span> : null}
                        </div>
                      </div>
                      {puedeEditar && (
                        <button className="btn btn--ghost" title="Cambiar a nombre de quién" style={{ fontSize: 11, padding: "0 6px", flexShrink: 0 }}
                          onClick={abrirEditorNombreFactura}>✎</button>
                      )}
                    </div>
                  )}

                  {/* Nota derivada */}
                  {objBlanco <= 0.01 && objNegro <= 0.01 ? (
                    <div style={{ fontSize: 12, color: "#94a3b8" }}>Registrá las cobranzas: la factura se arma con lo cobrado.</div>
                  ) : (
                    <div style={{ fontSize: 11, color: "#94a3b8" }}>
                      La factura se calcula con lo cobrado: transferencia/cheque → Finnegans, efectivo → aéreo.
                    </div>
                  )}

                  {seccionFacturas("blanco", "Finnegans (Marcela)", "Marcela", objBlanco, puedeMarcela, facturaRefBlanco)}
                  {seccionFacturas("negro", "Aéreo (Judy)", "Judy", objNegro, puedeFinanzas, facturaRefNegro)}
                </div>
              )}
            </div>
          );
        })()}

        <div style={cardStyle}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>PEDIDOS FINNEGANS</div>
          <div style={{ fontSize: 12, display: "grid", gap: 4 }}>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, background: "#e0f2fe", color: "#0369a1", borderRadius: 4, padding: "1px 6px", marginRight: 5 }}>R8</span>
              {obra.pedidoId
                ? <a href={`/pedidos/${encodeURIComponent(obra.pedidoId)}`} onClick={e => { e.preventDefault(); verProductosDelPedido(obra.pedidoId, pedidoVinculado); }} title="Ver los productos del pedido">{obra.pedidoId}</a>
                : <span style={{ color: "#c2410c" }}>sin vincular</span>}
            </div>
            <div>
              <span style={{ fontSize: 10, fontWeight: 700, background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "1px 6px", marginRight: 5 }}>GUALE</span>
              {obra.pedidoTelaId
                ? <a href={`/pedidos/${encodeURIComponent(obra.pedidoTelaId)}`} onClick={e => { e.preventDefault(); verProductosDelPedido(obra.pedidoTelaId, candidatos.find(p => p.id === obra.pedidoTelaId)); }} title="Ver los productos del pedido">{obra.pedidoTelaId}</a>
                : <span style={{ color: "#94a3b8" }}>sin vincular</span>}
            </div>
          </div>
        </div>

        <div style={cardStyle}>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>VERIFICACIONES</div>
          <div style={{ fontSize: 12, display: "grid", gap: 4 }}>
            {[
              { tipo: "op", label: "OP vs pedidos" },
              { tipo: "despacho", label: "Despacho R8" },
              { tipo: "despachoGuale", label: "Despacho Guale" },
            ].map(v => {
              const res = obra.verificaciones?.[v.tipo];
              const hayDoc = !!getArchivoObra(obra, v.tipo);
              return (
                <div
                  key={v.tipo}
                  onClick={() => hayDoc && obra.pedidoId && handleComparar(v.tipo)}
                  style={{ cursor: hayDoc && obra.pedidoId ? "pointer" : "default" }}
                  title={hayDoc ? "Ver el detalle de la comparación" : "Se verifica al adjuntar el documento"}
                >
                  {res ? (
                    <span style={{ color: res.ok ? "#15803d" : "#b91c1c", fontWeight: 600 }}>
                      {res.ok ? "✓" : "⚠️"} {v.label} · {res.texto}
                    </span>
                  ) : (
                    <span style={{ color: "#94a3b8" }}>— {v.label}{hayDoc ? " · tocá para verificar" : ""}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Gastos de la obra — solo finanzas (Andrés/Judy) */}
        {puedeFinanzas && (
          <div style={cardStyle}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>
              GASTOS DE LA OBRA
            </div>
            {gastos.length === 0 ? (
              <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 6 }}>
                Sin gastos cargados. Cargalos a medida que ocurren — alimentan el balance.
              </div>
            ) : (
              <div style={{ marginBottom: 6 }}>
                {gastos.map(g => (
                  <div key={g.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, padding: "2px 0", borderBottom: "1px solid #f8fafc" }}>
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#475569" }} title={`${g.concepto} · ${g.porNombre || ""}`}>
                      {g.concepto}
                    </span>
                    <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>$ {Number(g.monto).toLocaleString("es-UY", { maximumFractionDigits: 2 })}</span>
                    <button
                      className="btn btn--ghost" style={{ fontSize: 11, padding: "0 6px" }}
                      onClick={() => window.confirm(`¿Eliminar el gasto "${g.concepto}"?`) && eliminarGastoObra(obra.id, g.id)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, fontWeight: 700, paddingTop: 4, color: "#0f172a" }}>
                  <span>Total</span>
                  <span>$ {gastos.reduce((s, g) => s + (Number(g.monto) || 0), 0).toLocaleString("es-UY", { maximumFractionDigits: 2 })}</span>
                </div>
              </div>
            )}
            <form onSubmit={handleAgregarGasto} style={{ display: "grid", gap: 4 }}>
              <input
                list="conceptos-gasto"
                value={nuevoGasto.concepto}
                onChange={e => setNuevoGasto(g => ({ ...g, concepto: e.target.value }))}
                placeholder="Concepto (instalación, flete…)"
                style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1" }}
              />
              <datalist id="conceptos-gasto">
                {["Instalación", "Comisión", "Retiro vidrio", "Retiro accesorios", "Cortina", "Flete", "Extras"].map(c => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              <div style={{ display: "flex", gap: 4 }}>
                <input
                  type="number" step="0.01" min="0"
                  value={nuevoGasto.monto}
                  onChange={e => setNuevoGasto(g => ({ ...g, monto: e.target.value }))}
                  placeholder="Monto"
                  style={{ flex: 1, fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1", minWidth: 0 }}
                />
                <button type="submit" className="btn btn--outline" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
                  + Agregar
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Balance — resumen + botón al detalle (solo finanzas) */}
        {puedeFinanzas && (() => {
          const money = (v) => `${obra.monedaPresupuesto || "$"} ${Number(v).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`;
          const costoProduccion = costoProd && !costoProd.error ? costoProd.total : null;
          const totalGastos = gastos.reduce((s, g) => s + (Number(g.monto) || 0), 0);
          const costoTotal = (costoProduccion || 0) + totalGastos;
          const totalFacturado = Number(obra.montoFacturado) || 0;
          const precioVenta = totalFacturado > 0 ? totalFacturado : (Number(obra.montoPresupuesto) || 0);
          const ganancia = precioVenta - costoTotal;
          return (
            <div style={cardStyle}>
              <div style={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", letterSpacing: 0.5, marginBottom: 6 }}>BALANCE</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <span style={{ fontSize: 12.5, color: "#64748b" }}>Ganancia</span>
                <b style={{ fontSize: 15, color: ganancia >= 0 ? "#15803d" : "#b91c1c" }}>
                  {costoProduccion == null ? "…" : money(ganancia)}
                </b>
              </div>
              <button className="btn btn--outline" style={{ fontSize: 12, width: "100%" }} onClick={() => setShowBalance(true)}>
                Ver balance
              </button>
              {hitos.balance && (
                <div style={{ fontSize: 11, color: "#15803d", marginTop: 5, textAlign: "center" }}>
                  ✓ marcado por {obra.balanceHecho?.porNombre || "—"}
                </div>
              )}
            </div>
          );
        })()}
      </div>
      </div>{/* fin grid */}

      {/* ── Balance en modal (solo finanzas: Judy, Andrés, admin) ── */}
      {puedeFinanzas && showBalance && (() => {
        const money = (v) => `${obra.monedaPresupuesto || "$"} ${Number(v).toLocaleString("es-UY", { minimumFractionDigits: 2 })}`;
        const costoProduccion = costoProd && !costoProd.error ? costoProd.total : null;
        const totalGastos = gastos.reduce((s, g) => s + (Number(g.monto) || 0), 0);
        const costoTotal = (costoProduccion || 0) + totalGastos;
        // Precio de venta (neto): las facturas reemplazan al presupuesto si hay
        const totalFacturado = Number(obra.montoFacturado) || 0;
        const usaFacturas = totalFacturado > 0;
        const precioVenta = usaFacturas ? totalFacturado : (Number(obra.montoPresupuesto) || 0);
        const ganancia = precioVenta - costoTotal;
        return (
          <DetalleModal titulo="Balance de la obra" onClose={() => setShowBalance(false)}>
            <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
              Todos los valores son netos, sin IVA.
            </div>
            <div style={{ display: "grid", gap: 6, fontSize: 13 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "#64748b" }}>Precio de venta {usaFacturas ? "(facturado)" : "(presupuesto)"}</span>
                <b style={{ color: "#15803d" }}>{money(precioVenta)}</b>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "1px solid #f1f5f9", paddingTop: 5 }}>
                <span style={{ color: "#64748b" }}>Costo de productos (pedido OP)</span>
                <b>{costoProduccion != null ? money(costoProduccion) : "leyendo…"}</b>
              </div>
              {costoProd?.sinValor > 0 && <div style={{ fontSize: 11, color: "#c2410c" }}>{costoProd.sinValor} renglón(es) del OP sin valor legible</div>}
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>Gastos extra</span><b>{money(totalGastos)}</b></div>
              <div style={{ display: "flex", justifyContent: "space-between" }}><span style={{ color: "#64748b" }}>Costo total</span><b>{money(costoTotal)}</b></div>
              <div style={{ display: "flex", justifyContent: "space-between", borderTop: "2px solid #e2e8f0", paddingTop: 6, fontSize: 15 }}>
                <span style={{ fontWeight: 700 }}>Ganancia</span>
                <b style={{ color: ganancia >= 0 ? "#15803d" : "#b91c1c" }}>
                  {money(ganancia)}{costoTotal > 0 ? ` · ${Math.round((ganancia / costoTotal) * 100)}%` : ""}
                </b>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 14, paddingTop: 10, borderTop: "1px solid #f1f5f9", flexWrap: "wrap" }}>
              <span style={{ fontSize: 11.5, color: "#94a3b8", flex: 1, minWidth: 200 }}>
                Venta = {usaFacturas ? "suma de facturas" : "presupuesto"} · Costo = productos del pedido OP + gastos extra.
              </span>
              {hitos.balance
                ? <span style={{ fontSize: 11.5, color: "#15803d" }}>✓ marcado por {obra.balanceHecho?.porNombre || "—"}</span>
                : hitos.despacho && (
                  <button className="btn btn--outline" style={{ fontSize: 12, whiteSpace: "nowrap" }} onClick={() => handleBalance(true)}>Marcar balance hecho</button>
                )}
            </div>
          </DetalleModal>
        );
      })()}

      {/* ── Zona peligrosa (solo admin) ── */}
      {permisos.admin && (
        <div style={{ textAlign: "right", marginBottom: 20 }}>
          <button
            className="btn btn--ghost"
            style={{ color: "#b91c1c", fontSize: 12.5 }}
            disabled={busy}
            onClick={handleEliminar}
          >
            🗑️ Eliminar obra
          </button>
        </div>
      )}
    </div>
  );
}
